'use client';

import { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import { normalizeApiBaseUrl, getDefaultApiBaseUrl, getApiContextPath, normalizeWebSocketUrl } from './utils/url-normalization';
import { UserManager, User, WebStorageStateStore } from 'oidc-client-ts';
import { ErrorHandler } from './error-handler';
import { usePathname, useRouter } from 'next/navigation';
import { 
  SessionsApi, 
  TenantsApi,
  Configuration,
  type CreateSessionRequest,
  type SessionResponse,
  type MultiTenantSessionDto,
  type UserProfileDto,
  type TenantAccessDto,
  type SwitchTenantRequest,
  type TenantSwitchResponse
} from '@/lib/generated-api';
import { ResponseError } from '@/lib/generated-api';
import { validateSession, clearSessionData, persistSessionToStorage, applyCreatedSessionToClient, refreshSessionAccessToken } from './session-management';
import { readStoredAuthTokenJson } from './auth-token-storage';
import { setActiveSessionId, readSessionIdFromLocalStorage, resolveSessionIdForRequests, extractSessionId } from './session-id-store';
import { WebSocketService } from './websocket-service';
import { clearTimezoneCache, type SessionCleanupReason, clearAuthCookies } from './utils';
import { forceFullSignOut } from './force-full-sign-out';
import { wipeAppAuthState, clearOidcLocalStorageKeys, clearOidcUserLocalStorageKeys } from './wipe-app-auth-state';
import {
  applyOidcRenewal,
  isRefreshTokenDeadError,
  resolveOidcRenewalFailure,
  signinSilentSingleFlight,
  trySilentRenewAndApply,
} from './oidc-token-renewal';
import { useToast } from '@/components/ui/use-toast';
import { createCsrfEnabledConfig } from './csrf-enabled-client';
import { logger } from './logger';
import { constructOidcAuthorizationUrl, generateCodeVerifier, generateCodeChallenge, type OidcConfig } from './auth-config';
import { computeAuthPhase, getShouldRunValidation, VALIDATION_GRACE_MS } from './auth-state-machine';
import { isSecurityDashboardPdfPrintBrowserContext } from '@/lib/security-dashboard-pdf-injected-snapshot';
import {
  resolveLoginDomain,
  loginDomainConfigMatches,
  saveLoginDomainConfigForCallback,
  readLoginDomainConfigFromSessionStorage,
  clearLoginDomainConfigFromSessionStorage,
  mapDiscoveryToDomainConfig,
  saveOidcConfigForRestore,
  readOidcConfigForRestore,
  type LoginDomainConfig,
} from './login-domain';

interface UserProfile {
  user_id: string;
  email: string;
  name: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  preferred_username?: string;
  domain: string;
}

interface AuthConfig {
  authority?: string;
  client_id?: string;
  provider_type?: string;
  has_sso?: boolean;
  sso_provider?: string;
  keycloak_alias?: string;
  provider_config?: {
    sso_provider?: string;
    keycloak_alias?: string;
  };
}

interface ApiErrorResponse {
  response?: {
    status?: number;
    statusText?: string;
  };
}

interface DomainConfig {
  hasSSO: boolean;
  domain?: string;
  tenantId?: string;
  tenantName?: string;
  providerType?: string;
  error?: string;
  authConfig?: AuthConfig;
  auth_url?: string;
}

/** Single derived auth state for reliable UI and flow. See AUTH_DESIGN_AND_HARDENING.md */
export type AuthPhase =
  | 'initial'           // No user/session; show login
  | 'loading'           // Initial load or auth in progress (not callback)
  | 'callback'          // On /auth/callback or /auth/silent-callback
  | 'creating_session'  // User present, session being created
  | 'authenticated'     // user + session + currentTenant
  | 'expired'           // Error + no session (e.g. session expired; show login)
  | 'error';           // Other error (e.g. session exists but something failed)

interface AuthContextType {
  user: User | null;
  session: MultiTenantSessionDto | null;
  availableTenants: TenantAccessDto[];
  currentTenant: TenantAccessDto | null;
  lastTenantSwitchAt: number;
  isLoading: boolean;
  error: Error | null;
  userManager: UserManager | null;
  needsTenantSelection: boolean;
  needsEnrollment: boolean;
  pendingDomainConfig: any;
  dataRefreshTrigger: number;
  isSessionValid: boolean;
  isSessionValidating: boolean;
  /** Shared grace anchor used by consumers that validate session state. */
  sessionValidationStartedAtMs: number;
  /** Single derived phase for UI; use this instead of re-deriving from many booleans */
  authPhase: AuthPhase;
  login: (method: 'sso' | 'local' | 'auto', email?: string, domainConfig?: any) => void;
  logout: () => void;
  selectTenant: (tenantId: string) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<MultiTenantSessionDto>;
  refreshSessionAfterEnrollment: () => Promise<void>;
  /** Refetch session from server (e.g. after granting entitlement to current tenant). Use force: true to skip cooldown. */
  refreshSession: (options?: { force?: boolean }) => Promise<void>;
  remintSession: () => Promise<void>;
  ensureTenantAccess: (tenantId: string) => Promise<boolean>;
  updateSession: (sessionData: MultiTenantSessionDto) => void;
  /** Clear in-memory + storage session state (logout page, stale session recovery). */
  resetClientSession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Failsafe mechanism: Track consecutive authentication failures
const FAILSAFE_STORAGE_KEY = 'auth_failure_count';
const FAILSAFE_ERROR_STORAGE_KEY = 'auth_error_count';
const MAX_FAILURES = 3;

/**
 * Check if error is an "Access denied" authentication error
 * This checks for the specific "Access denied - no valid tenant access or tenant not activated" error
 */
function isAccessDeniedError(error: any): boolean {
  if (!error) return false;
  
  // Check various possible error message locations
  const errorMessage = (
    error?.error || 
    error?.message || 
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.body?.error ||
    error?.body?.message ||
    error?.response?.body?.error ||
    error?.response?.body?.message ||
    ''
  ).toLowerCase();
  
  // Check if it's the specific "Access denied - no valid tenant access or tenant not activated" error
  const isAccessDenied = errorMessage.includes('access denied');
  const hasTenantAccessIssue = errorMessage.includes('no valid tenant access') || 
                                errorMessage.includes('tenant not activated');
  
  // Also check for 403 status with access denied message
  const status = error?.status ?? error?.response?.status;
  const is403 = status === 403;
  
  return (isAccessDenied && hasTenantAccessIssue) || (is403 && isAccessDenied && hasTenantAccessIssue);
}

/**
 * Increment authentication failure counter
 */
function incrementAuthFailureCount(): number {
  try {
    const currentCount = parseInt(sessionStorage.getItem(FAILSAFE_STORAGE_KEY) || '0', 10);
    const newCount = currentCount + 1;
    sessionStorage.setItem(FAILSAFE_STORAGE_KEY, newCount.toString());
    logger.log(` [FAILSAFE] Authentication failure count: ${newCount}/${MAX_FAILURES}`);
    return newCount;
  } catch (e) {
    console.error(' [FAILSAFE] Failed to increment failure count:', e);
    return 1;
  }
}

/**
 * Increment authentication error display count (for UI error modal)
 */
function incrementAuthErrorCount(): number {
  try {
    const currentCount = parseInt(sessionStorage.getItem(FAILSAFE_ERROR_STORAGE_KEY) || '0', 10);
    const newCount = currentCount + 1;
    sessionStorage.setItem(FAILSAFE_ERROR_STORAGE_KEY, newCount.toString());
    return newCount;
  } catch (e) {
    console.error(' [FAILSAFE] Failed to increment error count:', e);
    return 1;
  }
}

/**
 * Reset authentication failure counters
 */
function resetAuthFailureCounts(): void {
  try {
    sessionStorage.removeItem(FAILSAFE_STORAGE_KEY);
    sessionStorage.removeItem(FAILSAFE_ERROR_STORAGE_KEY);
    logger.log(' [FAILSAFE] Reset authentication failure counters');
  } catch (e) {
    console.error(' [FAILSAFE] Failed to reset failure counts:', e);
  }
}

/**
 * Get current authentication failure count
 */
function getAuthFailureCount(): number {
  try {
    return parseInt(sessionStorage.getItem(FAILSAFE_STORAGE_KEY) || '0', 10);
  } catch (e) {
    return 0;
  }
}

/**
 * Get current authentication error count
 */
function getAuthErrorCount(): number {
  try {
    return parseInt(sessionStorage.getItem(FAILSAFE_ERROR_STORAGE_KEY) || '0', 10);
  } catch (e) {
    return 0;
  }
}

/** Get HTTP status from API error (e.g. ResponseError from createSession). */
function getHttpStatusFromError(e: unknown): number | undefined {
  if (e instanceof ResponseError && e.response) return e.response.status;
  const o = e as { status?: number; response?: { status?: number } };
  return o?.status ?? o?.response?.status;
}

/** True if error is a server error (5xx) or generic response error during session create. */
function isSessionCreationServerError(e: unknown): boolean {
  const status = getHttpStatusFromError(e);
  if (status != null && status >= 500) return true;
  // Generated API throws "Response returned an error code" for any non-2xx (e.g. 500)
  const msg = (e instanceof Error ? e.message : String(e)) || '';
  return msg.includes('Response returned an error code');
}

function readPreferredTenantId(): string | undefined {
  try {
    const preferred = localStorage.getItem('preferred_tenant_id');
    return preferred?.trim() ? preferred : undefined;
  } catch {
    return undefined;
  }
}

/** Returns preferred tenant id when it exists in `tenants`; clears stale localStorage value otherwise. */
function resolvePreferredTenantId(tenants: TenantAccessDto[]): string | undefined {
  const preferred = readPreferredTenantId();
  if (!preferred) return undefined;
  if (tenants.some((t) => t.tenantId === preferred)) {
    return preferred;
  }
  logger.log('AuthProviderClient: Clearing stale preferred_tenant_id not in available tenants:', preferred);
  try {
    localStorage.removeItem('preferred_tenant_id');
  } catch {}
  return undefined;
}

/** Prefer session.currentTenant over a stale `currentTenant` entry in localStorage. */
function reconcileCurrentTenantWithSession(
  sessionData: MultiTenantSessionDto,
  storedCurrentTenant: TenantAccessDto | null
): TenantAccessDto | null {
  const sessionTenant = sessionData.currentTenant ?? null;
  if (!sessionTenant?.tenantId) {
    return storedCurrentTenant;
  }
  if (!storedCurrentTenant?.tenantId || storedCurrentTenant.tenantId === sessionTenant.tenantId) {
    return sessionTenant;
  }
  logger.log('AuthProviderClient: Reconciling stale currentTenant with session binding', {
    storedTenantId: storedCurrentTenant.tenantId,
    sessionTenantId: sessionTenant.tenantId,
  });
  try {
    localStorage.setItem('currentTenant', JSON.stringify(sessionTenant));
  } catch {}
  return sessionTenant;
}

type E2eSessionBootstrap = {
  session: MultiTenantSessionDto | null;
  availableTenants: TenantAccessDto[];
  currentTenant: TenantAccessDto | null;
};

function isE2eSkipSessionMode(): boolean {
  return typeof window !== 'undefined' && localStorage.getItem('e2e_skip_session_websocket') === '1';
}

/** Playwright E2E: hydrate auth state synchronously from seeded localStorage before first paint. */
function readE2eSessionBootstrap(): E2eSessionBootstrap {
  const empty: E2eSessionBootstrap = {
    session: null,
    availableTenants: [],
    currentTenant: null,
  };
  if (typeof window === 'undefined') {
    return empty;
  }
  if (!isE2eSkipSessionMode()) {
    return empty;
  }
  try {
    const storedSession = localStorage.getItem('session');
    if (!storedSession?.trim()) {
      return empty;
    }
    const parsedSession = JSON.parse(storedSession) as MultiTenantSessionDto;
    const storedTenants = localStorage.getItem('availableTenants');
    const storedCurrentTenant = localStorage.getItem('currentTenant');
    const parsedTenants = storedTenants
      ? (JSON.parse(storedTenants) as TenantAccessDto[])
      : parsedSession.availableTenants || [];
    const parsedCurrentTenant = storedCurrentTenant
      ? (JSON.parse(storedCurrentTenant) as TenantAccessDto)
      : parsedSession.currentTenant ?? null;
    if (!parsedSession?.sessionId || !parsedCurrentTenant?.tenantId) {
      return empty;
    }
    return {
      session: parsedSession,
      availableTenants: parsedTenants,
      currentTenant: reconcileCurrentTenantWithSession(parsedSession, parsedCurrentTenant),
    };
  } catch {
    return empty;
  }
}

function AuthProviderClient({ children }: { children: ReactNode }) {
  logger.log('AuthProviderClient: Rendering...');
  const pathname = usePathname();
  const router = useRouter();
  const { toast } = useToast();

  // State management — start empty so SSR matches client hydration; E2E bootstrap runs in useLayoutEffect.
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<MultiTenantSessionDto | null>(null);
  const [availableTenants, setAvailableTenants] = useState<TenantAccessDto[]>([]);
  const [currentTenant, setCurrentTenant] = useState<TenantAccessDto | null>(null);
  const [lastTenantSwitchAt, setLastTenantSwitchAt] = useState<number>(0);
  const [needsTenantSelection, setNeedsTenantSelection] = useState(false);
  const [needsEmailEntry, setNeedsEmailEntry] = useState(true);
  const [needsEnrollment, setNeedsEnrollment] = useState(false);
  const [pendingDomainConfig, setPendingDomainConfig] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [userManager, setUserManager] = useState<UserManager | null>(null);
  const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0);
  const [isSessionValid, setIsSessionValid] = useState(false);
  const [isSessionValidating, setIsSessionValidating] = useState(false);
  const [sessionValidationStartedAtMs, setSessionValidationStartedAtMs] = useState(0);
  
  // Add flag to prevent duplicate session restoration
  const [sessionRestorationCompleted, setSessionRestorationCompleted] = useState(false);
  // Ref (not state) so nested createOrUpdateSession after createSession can proceed —
  // React state would still read `true` until the next render and abort the preferred-tenant switch.
  const sessionCreationInProgressRef = useRef(false);
  const [userProcessingCompleted, setUserProcessingCompleted] = useState(false);
  // Add flag to prevent session restoration after logout
  const [logoutInProgress, setLogoutInProgress] = useState(false);
  const prevTenantIdsRef = useRef<string[] | null>(null);
  const initialTenantsSetRef = useRef<boolean>(false);
  // Backoff state for token renewal retries to avoid CORS/503 spam
  const renewalBackoffRef = useRef<{ errorCount: number; nextRetryAt: number; warned: boolean }>({ errorCount: 0, nextRetryAt: 0, warned: false });
  const silentRenewInFlightRef = useRef<Promise<User | null> | null>(null);
  const handleTokenExpirationRef = useRef<() => Promise<void>>(async () => {});
  const handleSilentRenewalErrorRef = useRef<(error: unknown) => Promise<void>>(async () => {});
  // Debounce timer ref to prevent rapid successive session creation requests
  const sessionCreationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSessionRequestRef = useRef<{ user: User; tenants: TenantAccessDto[]; selectedTenantId: string } | null>(null);
  /** Timestamp when session was last created or restored; used to skip validation during grace period. */
  const sessionCreatedAtRef = useRef<number>(0);
  const webSocketServiceRef = useRef<WebSocketService | null>(null);
  const webSocketEffectRunIdRef = useRef(0);
  /** Tracks which sessionId the active WebSocket was opened for (detect stale connections). */
  const webSocketConnectedSessionIdRef = useRef<string | null>(null);
  /** Ensures sessionInvalid recovery (clear + redirect) runs once per burst (HTTP + WS). */
  const sessionInvalidRecoveryRef = useRef(false);
  /** When true, blocks localStorage persist effect during sign-out wipe. */
  const authStorageFrozenRef = useRef(false);

  // Playwright E2E: re-apply seeded localStorage after SSR hydration (useState init runs on server only).
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isE2eSkipSessionMode()) return;
    const boot = readE2eSessionBootstrap();
    if (!boot.session?.sessionId || !boot.currentTenant?.tenantId) return;
    setSession(boot.session);
    setAvailableTenants(boot.availableTenants);
    setCurrentTenant(boot.currentTenant);
    setNeedsEmailEntry(false);
    setNeedsTenantSelection(false);
    setNeedsEnrollment(false);
    setIsSessionValid(true);
    setSessionRestorationCompleted(true);
    setUserProcessingCompleted(true);
    setIsLoading(false);
  }, []);

  const beginSignOut = useCallback(() => {
    authStorageFrozenRef.current = true;
    setLogoutInProgress(true);
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem('logout_in_progress', 'true');
      } catch {
        /* ignore */
      }
    }
    if (sessionCreationTimerRef.current) {
      clearTimeout(sessionCreationTimerRef.current);
      sessionCreationTimerRef.current = null;
    }
    pendingSessionRequestRef.current = null;
  }, []);

  /**
   * Single place to clear auth state for re-login. Use this everywhere we force re-login (session
   * expired/revoked, loading timeout, failsafe, etc.) so all flags that block session creation are reset.
   * See AUTH_DESIGN_AND_HARDENING.md.
   *
   * Checklist of flags this resets (add any new flags here when adding state that affects re-login):
   * - clearSessionData() (localStorage: session, availableTenants)
   * - setSession(null)
   * - setUser(null) (prevents "Process user when loaded" from retrying session creation and causing an infinite loop)
   * - setAvailableTenants([])
   * - setCurrentTenant(null)
   * - setNeedsEmailEntry(true)
   * - setNeedsTenantSelection(false)
   * - setNeedsEnrollment(false)
   * - setUserProcessingCompleted(false)
   * - setIsSessionValid(false)
   * - optional: setError(optionalError)
   * - optional: setSessionRestorationCompleted(true) when options.preventRestoration
   * - when on /auth/callback or /auth/silent-callback: replace URL with / to prevent callback effect from re-running with same code (breaks infinite loop)
   * - sessionCreatedAtRef.current = 0 (so validation is not skipped after next session creation)
   */
  const clearAuthForReLogin = useCallback((
    optionalError?: Error,
    options?: { preventRestoration?: boolean }
  ) => {
    clearSessionData();
    setSession(null);
    setUser(null);
    setAvailableTenants([]);
    setCurrentTenant(null);
    setNeedsEmailEntry(true);
    setNeedsTenantSelection(false);
    setNeedsEnrollment(false);
    setUserProcessingCompleted(false);
    setIsSessionValid(false);
    sessionCreatedAtRef.current = 0;
    setSessionValidationStartedAtMs(0);
    if (optionalError) setError(optionalError);
    else setError(null);
    if (options?.preventRestoration) setSessionRestorationCompleted(true);
    if (typeof window !== 'undefined' && (window.location.pathname === '/auth/callback' || window.location.pathname === '/auth/silent-callback')) {
      window.history.replaceState({}, '', window.location.origin + '/');
    }
  }, []);

  /** Drop stale client session before login or OAuth callback (storage, cookies, React state, WebSocket). */
  const clearStaleClientSessionForLogin = useCallback((options?: { preserveOidcHandshake?: boolean }) => {
    const preserveOidcHandshake = options?.preserveOidcHandshake === true
    logger.log(
      preserveOidcHandshake
        ? ' [AUTH] Clearing stale Aegis session before OAuth callback (preserving OIDC PKCE/state)…'
        : ' [AUTH] Clearing stale client session before login/session create...'
    )
    // Best-effort destroy of residual Aegis session (H01) — fire-and-forget
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('session') : null
      const parsed = raw ? (JSON.parse(raw) as { sessionId?: string }) : null
      const staleSessionId = parsed?.sessionId
      if (staleSessionId) {
        const accessToken =
          (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('authToken') : null) ||
          (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null)
        let bearer: string | undefined
        try {
          bearer = accessToken ? (JSON.parse(accessToken) as { access_token?: string }).access_token : undefined
        } catch {
          bearer = undefined
        }
        const apiBase = (process.env.NEXT_PUBLIC_API_BASE_URL as string) || ''
        void fetch(`${apiBase.replace(/\/$/, '')}/v1/sessions/${encodeURIComponent(staleSessionId)}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
          },
        }).catch(() => undefined)
      }
    } catch {
      /* ignore */
    }
    clearSessionData()
    if (preserveOidcHandshake) {
      // Drop prior oidc.user profile only. Full oidc.* / sessionStorage wipe would destroy
      // the in-flight PKCE state + oidc_config_for_callback and break signinRedirectCallback.
      clearOidcUserLocalStorageKeys()
      clearAuthCookies()
    } else {
      clearOidcLocalStorageKeys()
      clearAuthCookies()
      void wipeAppAuthState({
        preserveTheme: true,
        setLogoutFlags: false,
        sessionMessage: null,
      }).catch(() => undefined)
    }
    setSession(null)
    setAvailableTenants([])
    setCurrentTenant(null)
    setIsSessionValid(false)
    sessionCreatedAtRef.current = 0
    if (webSocketServiceRef.current) {
      webSocketServiceRef.current.disconnect()
      webSocketServiceRef.current = null
    }
    webSocketConnectedSessionIdRef.current = null
  }, [])

  const triggerFullSignOut = useCallback(
    (
      reason: SessionCleanupReason,
      optionalError?: Error,
      options?: { skipIdpRedirect?: boolean }
    ) => {
      beginSignOut();
      void forceFullSignOut({
        reason,
        userManager,
        sessionId: session?.sessionId,
        idTokenHint: (user as User & { id_token?: string })?.id_token,
        skipIdpRedirect: options?.skipIdpRedirect ?? true,
        onBeforeStorageClear: () =>
          clearAuthForReLogin(
            optionalError ?? new Error('Session ended. Please sign in again.'),
            { preventRestoration: true }
          ),
        onAfterStorageClear: () => setUserManager(null),
      });
    },
    [userManager, session?.sessionId, user, clearAuthForReLogin, beginSignOut]
  )

  useEffect(() => {
    const onSessionInvalid = async (event: Event) => {
      if (sessionInvalidRecoveryRef.current) return
      if (isSecurityDashboardPdfPrintBrowserContext()) {
        logger.log(' [SESSION INVALID] Skipping recovery redirect — security dashboard PDF print (headless) context')
        return
      }
      const detail = (event as CustomEvent)?.detail as { source?: string } | undefined;
      if (detail?.source === 'websocket' && session?.sessionId) {
        sessionInvalidRecoveryRef.current = true;
        await resolveOidcRenewalFailure({
          sessionId: session.sessionId,
          error: new Error('Session expired or invalid. Please sign in again.'),
          validateSession,
          clearAuthForReLogin,
        });
        return;
      }
      sessionInvalidRecoveryRef.current = true;
      triggerFullSignOut('invalid', new Error('Session expired or invalid. Please sign in again.'));
    };
    if (typeof window === 'undefined') return;
    window.addEventListener('sessionInvalid', onSessionInvalid);
    return () => window.removeEventListener('sessionInvalid', onSessionInvalid);
  }, [triggerFullSignOut, session?.sessionId, clearAuthForReLogin]);

  /** After a successful session is established, allow a future sessionInvalid recovery. */
  useEffect(() => {
    if (session?.sessionId) {
      sessionInvalidRecoveryRef.current = false;
    }
  }, [session?.sessionId]);

  /** Failsafe re-login: full sign-out (Aegis + Keycloak) after max failures. */
  const runFailsafeReLogin = useCallback(() => {
    if (isSecurityDashboardPdfPrintBrowserContext()) {
      logger.log(' [FAILSAFE] Skipping failsafe redirect — security dashboard PDF print (headless) context')
      return
    }
    logger.log(' [FAILSAFE] Triggering full sign-out after max failures');
    try {
      sessionStorage.removeItem(FAILSAFE_STORAGE_KEY);
      sessionStorage.removeItem(FAILSAFE_ERROR_STORAGE_KEY);
    } catch (e) {
      console.error(' [FAILSAFE] Failed to clear failsafe keys:', e);
    }
    triggerFullSignOut('invalid', new Error('Too many failed attempts. Please sign in again.'));
  }, [triggerFullSignOut]);

  const markSessionValidationStartNow = useCallback(() => {
    const now = Date.now();
    sessionCreatedAtRef.current = now;
    setSessionValidationStartedAtMs(now);
  }, []);

  // Create API instances with auth configuration
  const createApiInstances = useCallback((authToken?: string) => {
    const envBase = process.env.NEXT_PUBLIC_API_BASE_URL as string;
    const contextPath = getApiContextPath();
    const { apiBase, rootBase } = normalizeApiBaseUrl(envBase, { contextPath });
    
    // Use CSRF-enabled configuration if auth token is available
    const config = authToken 
      ? createCsrfEnabledConfig(authToken, rootBase)
      : new Configuration({
          basePath: apiBase,
          headers: {
            'Content-Type': 'application/json'
          }
        });

    return {
      sessionsApi: new SessionsApi(config),
      tenantsApi: new TenantsApi(config)
    };
  }, []);

  // Secure token helpers (prefer sessionStorage; remove legacy localStorage copies)
  const setAuthToken = useCallback((token: { access_token: string; token_type: string }) => {
    try {
      sessionStorage.setItem('authToken', JSON.stringify(token));
      localStorage.removeItem('authToken');
      window.dispatchEvent(new CustomEvent('authTokenChanged', { detail: { authToken: token } }));
    } catch (e) {
      console.warn('AuthProviderClient: Failed to persist auth token to sessionStorage', e);
    }
  }, []);

  const getAuthToken = useCallback((): string | undefined => {
    try {
      const s = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
      if (!s) return undefined;
      const parsed = JSON.parse(s);
      return parsed?.access_token as string | undefined;
    } catch {
      return undefined;
    }
  }, []);

  // Schedule a throttled silent renew retry with exponential backoff and a one-time warning toast
  const scheduleSilentRenewRetry = useCallback(() => {
    const state = renewalBackoffRef.current;
    const now = Date.now();
    const baseDelayMs = 2000;
    const delay = Math.min(30000, baseDelayMs * Math.pow(2, state.errorCount || 0));
    state.errorCount = Math.min((state.errorCount || 0) + 1, 5);
    state.nextRetryAt = now + delay;

    if (!state.warned) {
      try {
        toast({
          title: 'Auth server temporarily unavailable',
          description: 'Token renewal will retry automatically.',
        });
      } catch {}
      state.warned = true;
    }

    setTimeout(async () => {
      if (!userManager) return;
      try {
        const retryUser = await signinSilentSingleFlight(userManager, silentRenewInFlightRef);
        if (applyOidcRenewal(retryUser, { setUser, setAuthToken })) {
          renewalBackoffRef.current.errorCount = 0;
          renewalBackoffRef.current.nextRetryAt = 0;
          renewalBackoffRef.current.warned = false;
        }
      } catch {
        // No-op: subsequent errors will be handled by the silent renew error handler
      }
    }, delay);
  }, [userManager, toast, setAuthToken]);

  // Handle user authentication and tenant discovery - memoized
  const handleUserLoaded = useCallback(async (user: User) => {
    try {
      logger.log('AuthProviderClient: handleUserLoaded called for:', user.profile.email);

      // Always clear dead client session before minting a new server session (DB may have been reset).
      clearStaleClientSessionForLogin();
      
      // Prevent multiple session creations
      if (sessionCreationInProgressRef.current) {
        logger.log('AuthProviderClient: Session creation already in progress, skipping');
        return;
      }
      
      
      // Race: OIDC UserLoaded can fire before React state has restored `session` from localStorage.
      // Treat a persisted session as authoritative so we do not POST createSession twice or stall on mocks/e2e.
      let storedSessionForGate: MultiTenantSessionDto | null = null;
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('session') : null;
        if (raw) storedSessionForGate = JSON.parse(raw) as MultiTenantSessionDto;
      } catch {
        storedSessionForGate = null;
      }

      const currentSession = session ?? storedSessionForGate;
      if (currentSession?.sessionId) {
        logger.log(
          'AuthProviderClient: Session already exists (React or localStorage), skipping session creation'
        );
        return;
      }
      
      sessionCreationInProgressRef.current = true;
      
      setIsLoading(true);
      setError(null);
      
      const email = user.profile.email as string;
      
      if (!email) {
        throw new Error('User email not available');
      }

      logger.log('AuthProviderClient: Creating session with JWT token for user:', email);
      
      // Store the JWT token for API authentication
      const jwtToken = user.access_token;
      if (!jwtToken) {
        throw new Error('No JWT token available from user object');
      }

      const authTokenData = {
        access_token: jwtToken,
        token_type: 'Bearer'
      };
      
      setAuthToken(authTokenData);
      logger.log('AuthProviderClient: JWT token stored for API authentication (sessionStorage)');
      
      // Trigger a custom event to notify services that auth token is available
      window.dispatchEvent(new CustomEvent('authTokenChanged', { 
        detail: { authToken: authTokenData } 
      }));
      
      // Create API instances with auth token
      const { sessionsApi } = createApiInstances(jwtToken);
      
      // Create user profile for our application
      logger.log('AuthProviderClient: Creating user profile...');
      logger.log(' [AUTH DEBUG] User profile data from OIDC:', {
        sub: user.profile.sub,
        preferred_username: user.profile.preferred_username,
        email: user.profile.email,
        name: user.profile.name,
        given_name: user.profile.given_name,
        family_name: user.profile.family_name,
        picture: user.profile.picture
      });
      
      const userProfile: UserProfileDto = {
        userId: user.profile.sub || user.profile.preferred_username || email,
        email,
        name: user.profile.name || (user.profile.given_name && user.profile.family_name ? 
          `${user.profile.given_name} ${user.profile.family_name}` : 
          user.profile.given_name || user.profile.family_name || email),
        givenName: user.profile.given_name,
        familyName: user.profile.family_name,
        picture: user.profile.picture,
        preferredUsername: user.profile.preferred_username,
      };
      
            logger.log(' [AUTH DEBUG] Created user profile for session:', userProfile);
      
      // Normalize user ID - some OIDC providers return IDs with special characters
      const rawUserId = user.profile.sub || user.profile.preferred_username || email;
      const normalizedUserId = rawUserId ? rawUserId.replace(/[^a-zA-Z0-9._-]/g, '_') : email;
      
      // Update user profile with normalized ID
      userProfile.userId = normalizedUserId;
      logger.log(' [AUTH DEBUG] Normalized user ID:', { original: rawUserId, normalized: normalizedUserId });
      
      const loginDomain = resolveLoginDomain(
        user.profile.preferred_username as string | undefined,
        email
      );
      logger.log(' [AUTH DEBUG] Resolved login domain from UPN/email:', loginDomain);

      const callbackDomainConfig = readLoginDomainConfigFromSessionStorage();
      const storedDomainConfig =
        pendingDomainConfig ||
        callbackDomainConfig ||
        (() => {
          const stored = localStorage.getItem('pendingDomainConfig');
          logger.log(' [AUTH DEBUG] Retrieved from localStorage:', stored);
          return stored ? JSON.parse(stored) : null;
        })();

      logger.log(' [AUTH DEBUG] Using domain config for session creation:', storedDomainConfig);
      logger.log(' [AUTH DEBUG] pendingDomainConfig state:', pendingDomainConfig);

      let finalDomainConfig = storedDomainConfig as LoginDomainConfig | null;
      if (
        finalDomainConfig &&
        loginDomain &&
        !loginDomainConfigMatches(finalDomainConfig, loginDomain)
      ) {
        logger.warn(
          'AuthProviderClient: Discarding stale domain config (stored domain does not match login UPN domain)',
          { stored: finalDomainConfig.domain, loginDomain }
        );
        finalDomainConfig = null;
      }

      if (!finalDomainConfig && loginDomain) {
        logger.log(' [AUTH DEBUG] No valid domain config — discovering by login UPN domain');
        try {
          const baseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL as string).replace(/\/$/, '');
          const contextPath = getApiContextPath();
          const { apiBase } = normalizeApiBaseUrl(baseUrl, { contextPath });
          const resp = await fetch(
            `${apiBase}/v1/auth/discover/${encodeURIComponent(loginDomain)}`,
            { credentials: 'omit' }
          );
          if (resp.ok) {
            const discovery: Record<string, unknown> = await resp.json();
            if (discovery && (discovery.customerId || discovery.found)) {
              finalDomainConfig = mapDiscoveryToDomainConfig(discovery, loginDomain);
              setPendingDomainConfig(finalDomainConfig);
              try {
                localStorage.setItem('pendingDomainConfig', JSON.stringify(finalDomainConfig));
              } catch {
                /* ignore */
              }
              logger.log(' [AUTH DEBUG] Domain discovery successful:', finalDomainConfig);
            }
          } else {
            console.warn('AuthProviderClient: Domain discovery failed with status', resp.status);
          }
        } catch (e) {
          console.warn('AuthProviderClient: Domain discovery threw (non-fatal):', e);
        }
      }

      if (finalDomainConfig?.domain && loginDomain) {
        finalDomainConfig = { ...finalDomainConfig, domain: loginDomain };
      }
      
      // Use preferred tenant from localStorage when it matches cached available tenants
      let resolvedTenantId: string | undefined = undefined;
      try {
        const preferred = readPreferredTenantId();
        if (preferred) {
          let cachedTenants: TenantAccessDto[] = [];
          try {
            const raw = localStorage.getItem('availableTenants');
            if (raw) cachedTenants = JSON.parse(raw);
          } catch {}
          if (Array.isArray(cachedTenants) && cachedTenants.length > 0) {
            resolvedTenantId = resolvePreferredTenantId(cachedTenants);
            if (resolvedTenantId) {
              logger.log('AuthProviderClient: Using validated preferred tenant from localStorage:', resolvedTenantId);
            } else {
              logger.log('AuthProviderClient: Ignoring stale preferred tenant (not in cached available tenants)');
            }
          } else {
            resolvedTenantId = preferred;
            logger.log('AuthProviderClient: Using preferred tenant from localStorage (no cached tenant list):', preferred);
          }
        } else {
          logger.log('AuthProviderClient: No preferred tenant found, will let session creation determine tenant');
        }
      } catch (e) {
        console.warn('AuthProviderClient: Failed to get preferred tenant from localStorage:', e);
      }

      // Create session request using generated API types
      const sessionRequest: CreateSessionRequest = {
        userProfile,
        currentTenantId: resolvedTenantId,
        metadata: {
          user_agent: navigator.userAgent,
          login_method: 'oidc',
          domain: finalDomainConfig?.domain,
          customerId: finalDomainConfig?.customerId,
          providerType: finalDomainConfig?.providerType,
          sso_provider: finalDomainConfig?.providerType,
          domain_mapping_customer_id: finalDomainConfig?.customerId,
        },
      };

      logger.log('AuthProviderClient (handleUserLoaded): Calling sessionsApi.createSession...');
      logger.log(' [AUTH DEBUG] Session request details:', {
        userProfile: userProfile,
        currentTenantId: sessionRequest.currentTenantId,
        metadata: sessionRequest.metadata,
        hasCustomerId: !!storedDomainConfig?.customerId,
        customerId: storedDomainConfig?.customerId,
        domainConfigHints: {
          primaryTenantId: storedDomainConfig?.primaryTenantId,
          tenantId: storedDomainConfig?.tenantId,
          domain: storedDomainConfig?.domain
        }
      });
      
      let newSession: MultiTenantSessionDto | undefined = undefined;
      
      try {
        // Retry session creation if backend has not yet finalized domain→tenant bindings
        const maxAttempts = 12;
        const baseDelayMs = 400;
        let lastError: any = null;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            const rawResponse = await sessionsApi.createSessionRaw({
              createSessionRequest: sessionRequest
            });
            const sessionResponse: SessionResponse = await rawResponse.value();
            const setCookieHeader = rawResponse.raw.headers.get('set-cookie');

            logger.log(' [AUTH DEBUG] Session creation response:', {
              success: sessionResponse.success,
              error: sessionResponse.error,
              hasData: !!sessionResponse.data,
              dataKeys: sessionResponse.data ? Object.keys(sessionResponse.data) : [],
              availableTenants: sessionResponse.data?.availableTenants?.length || 0,
              attempt
            });

            if (sessionResponse.success && sessionResponse.data) {
              const createdSession = applyCreatedSessionToClient({
                session: sessionResponse.data,
                availableTenants: sessionResponse.data.availableTenants,
                currentTenant: sessionResponse.data.currentTenant,
                setCookieHeader,
              });
              newSession = createdSession;
              // Reset failure counters on successful session creation
              resetAuthFailureCounts();
              // Apply session to React state BEFORE any await/validation so parallel
              // validateSession/getSession calls cannot use a stale sessionId in the URL path.
              setSession(createdSession);
              markSessionValidationStartNow();
              logger.debug('[AUTH] createSession succeeded', {
                hasSessionId: !!createdSession.sessionId,
              });
              break;
            }

            // Not successful → decide whether to retry or bail
            const responseAny = sessionResponse as any;
            const backendErrorCode = String(responseAny?.errorCode || '').toUpperCase();
            const backendRetryable = responseAny?.retryable === true;
            const errText = (sessionResponse.error || '').toLowerCase();
            if (backendErrorCode === 'SESSION_INVALID') {
              logger.log(' [AUTH] Backend reported SESSION_INVALID, clearing auth and restarting login');
              triggerFullSignOut('invalid', new Error(responseAny?.message || 'Session expired or invalid. Please sign in again.'));
              return;
            }
            if (backendErrorCode === 'TENANT_UNAUTHORIZED') {
              // Non-retryable authorization path: stop retry loop and let downstream no-access UX handle it.
              throw new Error(sessionResponse.error || responseAny?.message || 'Access denied - no valid tenant access');
            }
            if (backendErrorCode === 'SESSION_CREATION_FAILED' && !backendRetryable) {
              const duplicateSession =
                errText.includes('session_id') ||
                errText.includes('duplicate') ||
                errText.includes('already exists');
              throw new Error(
                duplicateSession
                  ? 'A session already exists for this browser. Refresh the page and sign in again.'
                  : (sessionResponse.error || responseAny?.message || 'Failed to create session')
              );
            }
            if (backendErrorCode === 'TRANSIENT_RETRYABLE' && backendRetryable && attempt < maxAttempts - 1) {
              const backoff = Math.min(baseDelayMs * Math.pow(1.5, attempt), 2000);
              await new Promise(r => setTimeout(r, backoff));
              continue;
            }
            if ((errText.includes('no tenant access') || errText.includes('forbidden') || errText.includes('403')) && attempt < maxAttempts - 1) {
              const backoff = Math.min(baseDelayMs * Math.pow(1.5, attempt), 2000);
              await new Promise(r => setTimeout(r, backoff));
              continue;
            }
            if (!sessionResponse.success) {
              throw new Error(sessionResponse.error || 'Failed to create session');
            }
          } catch (e: any) {
            lastError = e;
            const status = e?.status ?? e?.response?.status;
            const errorCode = String(e?.errorCode || e?.response?.data?.errorCode || '').toUpperCase();
            
            // CRITICAL FAILSAFE: Check for "Access denied" errors before retrying
            if (isAccessDeniedError(e)) {
              const failureCount = incrementAuthFailureCount();
              logger.log(` [FAILSAFE] Access denied error in createSession retry loop. Failure count: ${failureCount}/${MAX_FAILURES}`);
              
              // If we've failed 3 times, trigger failsafe cleanup
              if (failureCount >= MAX_FAILURES) {
                logger.log(' [FAILSAFE] Maximum failures reached in createSession - clearing session and redirecting to login');
                runFailsafeReLogin();
                return;
              }
            }

            const requestUrl = String(e?.url || e?.response?.config?.url || '').toLowerCase();
            const isSessionEndpoint = requestUrl.includes('/api/v1/sessions/');
            if (
              (status === 401 && errorCode === 'SESSION_INVALID') ||
              (status === 401 && !errorCode && isSessionEndpoint)
            ) {
              logger.log(' [AUTH] Session invalid 401 returned from backend, forcing full auth reset', {
                errorCode: errorCode || 'missing',
                requestUrl,
              });
              triggerFullSignOut('invalid', new Error('Session expired or invalid. Please sign in again.'));
              return;
            }
            
            if ((status === 403 || (e?.message || '').toLowerCase().includes('no tenant access')) && attempt < maxAttempts - 1) {
              const backoff = Math.min(baseDelayMs * Math.pow(1.5, attempt), 2000);
              await new Promise(r => setTimeout(r, backoff));
              continue;
            }
            // Server error (5xx) or generic response error: redirect to login so user can try again
            if (isSessionCreationServerError(e)) {
              logger.log(' [AUTH] Session creation failed with server error, redirecting to login');
              triggerFullSignOut('session_creation_failed');
              return;
            }
            // Non-retryable
            throw e;
          }
        }

        if (!newSession) {
          // Backend returned "temporarily failed" (e.g. stale session after restart): redirect to sign in
          const errMsg = (lastError instanceof Error ? lastError.message : String(lastError ?? '')) || '';
          if (errMsg.includes('temporarily failed') || errMsg.includes('Please sign in again')) {
            logger.log(' [AUTH] Session creation temporarily failed (backend), redirecting to login');
            triggerFullSignOut('session_creation_failed');
            return;
          }
          // Still no session after retries
          const hasMappedDomain = !!finalDomainConfig?.customerId;
          logger.log('AuthProviderClient: No tenant access after retries', { hasMappedDomain });
          const minimalSession = {
            sessionId: undefined,
            profile: userProfile,
            currentTenant: undefined,
            availableTenants: [],
            selectedTenantIds: undefined,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            expiresAt: Date.now() + (24 * 60 * 60 * 1000),
            lastActivity: new Date().toISOString(),
            metadata: { login_method: 'oidc' }
          };
          setSession(minimalSession as any);
          setAvailableTenants([]);
          setCurrentTenant(null);
          setNeedsTenantSelection(false);
          setNeedsEmailEntry(false);
          setIsLoading(false);
          setPendingDomainConfig(null);
          localStorage.removeItem('pendingDomainConfig');
          if (!hasMappedDomain) {
            const userDomain = user.profile.email?.split('@')[1] || '';
            router.push(`/enroll?domain=${encodeURIComponent(userDomain)}`);
          } else {
            try {
              // Non-invasive notification; user can retry login
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (window as any).toast?.({ title: 'Provisioning access', description: 'Please try again in a few seconds.' });
            } catch {}
          }
          return;
        }
        logger.log('AuthProviderClient: Session created successfully with tenant info:', newSession);

        // DEBUG: Log the full session structure to understand permission format
        logger.log(' [AUTH DEBUG] Full session response structure:', {
          sessionId: newSession.sessionId,
          hasCurrentTenantPermissions: !!newSession.currentTenantPermissions,
          hasEffectivePermissions: !!newSession.effectivePermissions,
          hasCurrentTenant: !!newSession.currentTenant,
          currentTenantKeys: newSession.currentTenant ? Object.keys(newSession.currentTenant) : [],
          currentTenantPermissions: newSession.currentTenant?.permissions,
          currentTenantPermissionsType: typeof newSession.currentTenant?.permissions,
          currentTenantPermissionsLength: newSession.currentTenant?.permissions?.length || 0,
          sessionKeys: Object.keys(newSession),
          currentTenantPermissionsKeys: newSession.currentTenantPermissions ? Object.keys(newSession.currentTenantPermissions) : []
        });

        // CRITICAL: Validate that the session has permissions
        const hasPermissions = !!(
          newSession.currentTenantPermissions || 
          newSession.effectivePermissions || 
          newSession.currentTenant?.permissions
        );

        if (!hasPermissions) {
          console.error('❌ [AUTH] Created session lacks permissions!');
          logger.log(' [AUTH] Session structure:', {
            sessionId: newSession.sessionId,
            hasCurrentTenantPermissions: !!newSession.currentTenantPermissions,
            hasEffectivePermissions: !!newSession.effectivePermissions,
            hasCurrentTenant: !!newSession.currentTenant,
            currentTenantPermissions: newSession.currentTenant?.permissions
          });
          
          // This is a critical error - the backend should always return permissions
          throw new Error('Session created without permissions - please contact administrator');
        }

        logger.log(' [AUTH] Session has permissions:', {
          hasCurrentTenantPermissions: !!newSession.currentTenantPermissions,
          hasEffectivePermissions: !!newSession.effectivePermissions,
          hasCurrentTenantPermissions_simple: !!newSession.currentTenant?.permissions
        });
        
        // Clear stored domain configuration after successful session creation
        setPendingDomainConfig(null);
        localStorage.removeItem('pendingDomainConfig');
        clearLoginDomainConfigFromSessionStorage();
        
        // Extract available tenants from session response
        const availableTenants = newSession.availableTenants || [];
        logger.log('AuthProviderClient: Available tenants from session:', availableTenants);
        setAvailableTenants(availableTenants);

        if (availableTenants.length === 0) {
          throw new Error('User is not authorized for any tenants');
        }

        // Set current tenant from session
        if (newSession.currentTenant) {
          setCurrentTenant(newSession.currentTenant);
          logger.log('AuthProviderClient: Current tenant set from session:', newSession.currentTenant.tenantId);
        }

        // Check if user needs to select a tenant
        const requiresTenantSelection =
          newSession.requiresTenantSelection === true
          || newSession.metadata?.requires_tenant_selection === true;
        const explicitPrimaryBinding =
          newSession.explicitPrimaryBinding === true
          || newSession.metadata?.explicit_primary_binding === true;

        if (availableTenants.length > 1) {
          // Check for stored tenant preference (ignore stale ids from deleted tenants)
          const preferredId = resolvePreferredTenantId(availableTenants);
          const preferredTenant = preferredId
            ? availableTenants.find((t) => t.tenantId === preferredId)
            : undefined;
          
          if (preferredTenant && newSession.currentTenant?.tenantId !== preferredTenant.tenantId) {
            logger.log('AuthProviderClient: Multiple tenants - switching to preferred:', preferredTenant.tenantId);
            // Create finished; release the in-progress lock so the preferred-tenant switch can run.
            sessionCreationInProgressRef.current = false;
            await createOrUpdateSession(user, availableTenants, preferredTenant.tenantId!);
          } else if (requiresTenantSelection && !preferredTenant) {
            logger.log('AuthProviderClient: Multiple tenants - backend requires tenant selection');
            setNeedsTenantSelection(true);
            setNeedsEmailEntry(false);
            setError(null);
            setIsLoading(false);
          } else if (!preferredTenant && explicitPrimaryBinding && newSession.currentTenant?.tenantId) {
            try {
              toast({
                title: 'Switching to primary tenant…',
                description: newSession.currentTenant.tenantName || newSession.currentTenant.tenantSlug,
              });
            } catch {}
            logger.log('AuthProviderClient: Multiple tenants - explicit primary binding, using currentTenant');
            localStorage.setItem('preferred_tenant_id', newSession.currentTenant.tenantId!);
            setNeedsTenantSelection(false);
            setNeedsEmailEntry(false);
            setError(null);
            setIsLoading(false);
          } else if (!preferredTenant && newSession.currentTenant?.tenantId) {
            // Backend already bound a current tenant — proceed (do not force picker).
            // Forcing needsTenantSelection here deadlocks AuthEnforcer: authPhase is
            // 'authenticated' (AppLayout + blank `/`) while canonicalize bails on the flag
            // and the picker UI never renders (authenticated branch wins).
            logger.log(
              'AuthProviderClient: Multiple tenants - no preferred id, using session currentTenant:',
              newSession.currentTenant.tenantId
            );
            localStorage.setItem('preferred_tenant_id', newSession.currentTenant.tenantId!);
            setNeedsTenantSelection(false);
            setNeedsEmailEntry(false);
            setError(null);
            setIsLoading(false);
          } else if (!preferredTenant) {
            logger.log('AuthProviderClient: Multiple tenants - no currentTenant, showing selector');
            setNeedsTenantSelection(true);
            setNeedsEmailEntry(false);
            setError(null);
            setIsLoading(false);
          } else {
            logger.log('AuthProviderClient: Multiple tenants - using current tenant from session');
            setNeedsTenantSelection(false);
            setNeedsEmailEntry(false);
            setError(null);
            setIsLoading(false);
          }
        } else {
          // Single tenant - use the one from session
          logger.log('AuthProviderClient: Single tenant - using tenant from session');
          setNeedsTenantSelection(false);
          setNeedsEmailEntry(false);
          setError(null);
          setIsLoading(false);
          
          // Store tenant preference
          if (newSession.currentTenant) {
            localStorage.setItem('preferred_tenant_id', newSession.currentTenant.tenantId!);
          }
        }
        
      } catch (sessionError) {
        // Handle session creation errors more gracefully
        logger.log('AuthProviderClient: Session creation result:', sessionError);
        
        // Check if this is an HTTP 403 error (no tenant access) - this is expected for new users
        const error = sessionError as any;
        const errorStatus = error.status ?? error.response?.status;
        const is403 = errorStatus === 403;
        
        logger.log(' [AUTH DEBUG] Session creation error details:', {
          status: error.status,
          responseStatus: errorStatus,
          error: error.message || error,
          errorType: typeof error
        });
        
        // CRITICAL FAILSAFE: If we get a 403 and already have a session, this might be the "Access denied" loop
        // Check if we have an existing session - if so, this 403 is likely the access denied error
        // Access session from the state directly to avoid TypeScript inference issues in catch block
        const currentSessionState = session;
        const hasExistingSession = currentSessionState !== null && 
                                   typeof currentSessionState === 'object' && 
                                   'sessionId' in currentSessionState && 
                                   !!(currentSessionState as MultiTenantSessionDto).sessionId;
        
        if (is403 && hasExistingSession) {
          // This is likely the "Access denied - no valid tenant access" error causing a loop
          const failureCount = incrementAuthFailureCount();
          const errorCount = incrementAuthErrorCount();
          
          logger.log(` [FAILSAFE] 403 error with existing session detected (likely access denied loop). Failure count: ${failureCount}/${MAX_FAILURES}, Error count: ${errorCount}/${MAX_FAILURES}`);
          
          // If we've failed 3 times, trigger failsafe cleanup
          if (failureCount >= MAX_FAILURES && errorCount >= MAX_FAILURES) {
            logger.log(' [FAILSAFE] Maximum failures reached in handleUserLoaded - clearing session and redirecting to login');
            runFailsafeReLogin();
            return;
          }
          
          // Set error but allow retries up to 3
          setError(new Error('Authentication timeout - please try again'));
          setIsLoading(false);
          return;
        }
        
        // Also check for access denied error using the standard function
        if (isAccessDeniedError(error)) {
          const failureCount = incrementAuthFailureCount();
          const errorCount = incrementAuthErrorCount();
          
          logger.log(` [FAILSAFE] Access denied error detected (standard check). Failure count: ${failureCount}/${MAX_FAILURES}, Error count: ${errorCount}/${MAX_FAILURES}`);
          
          if (failureCount >= MAX_FAILURES && errorCount >= MAX_FAILURES) {
            logger.log(' [FAILSAFE] Maximum failures reached - clearing session and redirecting to login');
            runFailsafeReLogin();
            return;
          }
          
          setError(new Error('Authentication timeout - please try again'));
          setIsLoading(false);
          return;
        }
        
        if (is403) {
          logger.log('AuthProviderClient: HTTP 403 - User has no tenant access, redirecting to enrollment');
          
          // Store minimal session info for enrollment
          const minimalSession = {
            sessionId: undefined,
            profile: userProfile,
            currentTenant: undefined,
            availableTenants: [],
            selectedTenantIds: undefined,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
            lastActivity: new Date().toISOString(),
            metadata: { login_method: 'oidc' }
          };
          
          setSession(minimalSession as any);
          setAvailableTenants([]);
          setCurrentTenant(null);
          setNeedsTenantSelection(false);
          setNeedsEmailEntry(false);
          setIsLoading(false);
          
          // Clear stored domain configuration
          setPendingDomainConfig(null);
          localStorage.removeItem('pendingDomainConfig');
          
          // Extract domain from user email for enrollment
          const userDomain = user.profile.email?.split('@')[1] || '';
          
          // Redirect to enrollment page with domain prefilled
          router.push(`/enroll?domain=${encodeURIComponent(userDomain)}`);
          return;
        }
        
        // Server error (5xx) or generic response error: redirect to login so user can try again (e.g. after frontend restart)
        if (isSessionCreationServerError(sessionError)) {
          logger.log(' [AUTH] Session creation failed with server error, redirecting to login');
          triggerFullSignOut('session_creation_failed');
          return;
        }
        // For other errors, log them as actual errors
        console.error('AuthProviderClient: Error handling user load:', sessionError);
        setError(sessionError instanceof Error ? sessionError : new Error('Failed to load user tenants'));
        setIsLoading(false);
      }
      
          } catch (err) {
        if (isSessionCreationServerError(err)) {
          logger.log(' [AUTH] Session creation failed with server error, redirecting to login');
          triggerFullSignOut('session_creation_failed');
          return;
        }
        console.error('AuthProviderClient: Error handling user load:', err);
        setError(err instanceof Error ? err : new Error('Failed to load user tenants'));
        setIsLoading(false);
      } finally {
        sessionCreationInProgressRef.current = false;
      }
  }, [createApiInstances, router, runFailsafeReLogin, clearStaleClientSessionForLogin, triggerFullSignOut]);

  /**
   * Single entry point for "user present, need session". Call from OIDC callback and from
   * "Process user when loaded" effect so session creation is never skipped. Manages
   * userProcessingCompleted to prevent duplicate calls. See AUTH_DESIGN_AND_HARDENING.md.
   */
  const ensureSessionForUser = useCallback(async (u: User) => {
    setUserProcessingCompleted(true);
    try {
      await handleUserLoaded(u);
    } catch (err) {
      setUserProcessingCompleted(false);
      setError(err instanceof Error ? err : new Error('Failed to load user tenants'));
      setIsLoading(false);
    }
  }, [handleUserLoaded]);

  // Create UserManager with configuration
  const createUserManager = useCallback((authUrl?: string) => {
    const authority = authUrl || process.env.NEXT_PUBLIC_OIDC_AUTHORITY || "";
    const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID || "";

    if (!authority || !clientId) {
      const msg = [
        "❌ OIDC CONFIGURATION ERROR:",
        `NEXT_PUBLIC_OIDC_AUTHORITY: "${authority}"`,
        `NEXT_PUBLIC_OIDC_CLIENT_ID: "${clientId}"`,
        "",
        "You must set both OIDC_AUTHORITY and OIDC_CLIENT_ID in your environment variables.",
        "Example:",
        "  NEXT_PUBLIC_OIDC_AUTHORITY=https://your-keycloak-domain/realms/your-realm",
        "  NEXT_PUBLIC_OIDC_CLIENT_ID=your-client-id",
      ].join("\n");

      // Log to console for devs
      console.error(msg);
      
      // Show a visible alert for users
      if (typeof window !== "undefined") {
        alert(msg);
      }
      throw new Error("Missing NEXT_PUBLIC_OIDC_AUTHORITY or NEXT_PUBLIC_OIDC_CLIENT_ID");
    }

    const settings = {
      authority,
      client_id: clientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/auth/logout`,
      response_type: 'code',
      scope: 'openid profile email',
      automaticSilentRenew: false,
      silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      filterProtocolClaims: true,
      loadUserInfo: true,
      monitorSession: false,
      checkSessionInterval: 60,
      // Add these settings for better token management
      accessTokenExpiringNotificationTime: 60, // Notify 60 seconds before expiry
      includeIdTokenInSilentRenew: false, // Only renew access token
      // Enable PKCE (required by Keycloak)
      code_challenge_method: 'S256',
    };

    logger.log('Creating UserManager with settings:', settings);
    const userManager = new UserManager(settings);
    
    // Add event listeners for better debugging
    userManager.events.addUserLoaded((user) => {
      logger.log(' [OIDC] User loaded event:', user.profile.email);
    });
    
    userManager.events.addUserUnloaded(() => {
      logger.log(' [OIDC] User unloaded event');
    });
    
    userManager.events.addSilentRenewError((error) => {
      console.error('❌ [OIDC] Silent renewal error:', error);
      void handleSilentRenewalErrorRef.current(error);
    });

    // Add error handling for session creation failures
    userManager.events.addUserLoaded((user) => {
      logger.log(' [OIDC] User loaded event:', user.profile.email);
      if (isE2eSkipSessionMode()) return;
      setUser(user); // Just set the user, don't call handleUserLoaded here
    });
    
    userManager.events.addAccessTokenExpiring(() => {
      logger.log(' [OIDC] Access token expiring, attempting silent renewal...');
    });
    
    userManager.events.addAccessTokenExpired(() => {
      logger.log('❌ [OIDC] Access token expired');
      void handleTokenExpirationRef.current();
    });
    
    return userManager;
  }, []);

  // Session refresh cooldown to prevent excessive API calls
  const lastSessionRefreshRef = useRef<number>(0);
  const SESSION_REFRESH_COOLDOWN = 60000; // 60 seconds

  // Refresh session data after token renewal (or explicit refreshSession({ force: true }))
  const refreshSessionData = useCallback(async (options?: { force?: boolean }) => {
    const authoritativeSessionId = resolveSessionIdForRequests() ?? session?.sessionId;
    if (!authoritativeSessionId) {
      logger.log(' [SESSION REFRESH] No existing session to refresh - skipping');
      return;
    }

    const now = Date.now();
    const timeSinceLastRefresh = now - lastSessionRefreshRef.current;
    const skipCooldown = options?.force === true;

    if (!skipCooldown && timeSinceLastRefresh < SESSION_REFRESH_COOLDOWN) {
      logger.log(` [SESSION REFRESH] Skipping refresh - cooldown active (${Math.round((SESSION_REFRESH_COOLDOWN - timeSinceLastRefresh) / 1000)}s remaining)`);
      return;
    }

    try {
      logger.debug(' [SESSION REFRESH] Refreshing session data');
      lastSessionRefreshRef.current = now;
      
      // Get the current auth token
      const authToken = getAuthToken();
      if (!authToken) {
        console.warn(' [SESSION REFRESH] No auth token available for session refresh');
        return;
      }

      // Create API instances with the new token
      const { sessionsApi } = createApiInstances(authToken);
      
      // Get the current session data from the backend
      const sessionResponse = await sessionsApi.getSession({
        sessionId: authoritativeSessionId
      });

      // CRITICAL FAILSAFE: Check for "Access denied" errors
      if (!sessionResponse.success && isAccessDeniedError(sessionResponse)) {
        const failureCount = incrementAuthFailureCount();
        logger.log(` [FAILSAFE] Access denied error in refreshSession. Failure count: ${failureCount}/${MAX_FAILURES}`);
        
        if (failureCount >= MAX_FAILURES) {
          logger.log(' [FAILSAFE] Maximum failures reached in refreshSession - clearing session and redirecting to login');
          runFailsafeReLogin();
          return;
        }
        
        // Don't throw error, just return - let the failsafe handle it
        return;
      }

      if (sessionResponse.success && sessionResponse.data) {
        logger.log(' [SESSION REFRESH] Session data refreshed successfully');
        
        // DEBUG: Log what backend is returning
        logger.log(' [SESSION REFRESH] Backend session data:', {
          sessionId: sessionResponse.data.sessionId,
          availableTenantsCount: sessionResponse.data.availableTenants?.length ?? 0,
          availableTenantIds: sessionResponse.data.availableTenants?.map(t => t.tenantId) ?? [],
          selectableTenantIdsCount: sessionResponse.data.selectableTenantIds?.length ?? 0,
          selectableTenantIds: sessionResponse.data.selectableTenantIds ?? [],
          currentTenantId: sessionResponse.data.currentTenant?.tenantId
        });
        
        // Reset failure counters on successful session refresh
        resetAuthFailureCounts();
        
        // Update the session with fresh data
        setSession(sessionResponse.data);
        
        // Update available tenants if they changed
        if (sessionResponse.data.availableTenants) {
          setAvailableTenants(sessionResponse.data.availableTenants);
        }
        
        // Update current tenant if it changed
        if (sessionResponse.data.currentTenant) {
          setCurrentTenant(sessionResponse.data.currentTenant);
        }

        // Clear stale errors so AuthEnforcer can canonicalize after a successful refresh
        setError(null);
        
        logger.log(' [SESSION REFRESH] Session, tenants, and permissions updated');
      } else {
        console.warn(' [SESSION REFRESH] Failed to refresh session data:', sessionResponse.error);
        
        // If session not found, clear auth state for re-login (resets all flags; see clearAuthForReLogin checklist)
        if (sessionResponse.error && sessionResponse.error.includes('Session not found')) {
          logger.log('🧹 [SESSION REFRESH] Session not found on backend, clearing local session state');
          triggerFullSignOut('expired', new Error('Session not found. Please sign in again.'));
        }
      }
    } catch (error) {
      console.error('❌ [SESSION REFRESH] Error refreshing session data:', error);
    }
  }, [session?.sessionId, createApiInstances, getAuthToken, runFailsafeReLogin, triggerFullSignOut]);

  // Update session with returned data (e.g., from selectTenants)
  const updateSession = useCallback((sessionData: MultiTenantSessionDto) => {
    logger.log(' [SESSION UPDATE] Updating session with returned data:', sessionData.sessionId);
    
    // DEBUG: Log what we're receiving
    logger.log(' [SESSION UPDATE] Session data being applied:', {
      sessionId: sessionData.sessionId,
      availableTenantsCount: sessionData.availableTenants?.length ?? 0,
      availableTenantIds: sessionData.availableTenants?.map(t => t.tenantId) ?? [],
      selectableTenantIdsCount: sessionData.selectableTenantIds?.length ?? 0,
      selectableTenantIds: sessionData.selectableTenantIds ?? [],
      currentTenantId: sessionData.currentTenant?.tenantId
    });
    
    // Update the session state
    setSession(sessionData);
    
    // Update available tenants if they changed
    if (sessionData.availableTenants) {
      logger.log(' [SESSION UPDATE] Setting availableTenants from sessionData:', {
        count: sessionData.availableTenants.length,
        tenantIds: sessionData.availableTenants.map(t => t.tenantId)
      });
      setAvailableTenants(sessionData.availableTenants);
    } else {
      logger.log(' [SESSION UPDATE] WARNING: sessionData has no availableTenants');
    }
    
    // Update current tenant if it changed
    if (sessionData.currentTenant) {
      setCurrentTenant(sessionData.currentTenant);
    }

    setError(null);
    
    // Store in localStorage for persistence
    persistSessionToStorage({
      session: sessionData,
      availableTenants: sessionData.availableTenants,
      currentTenant: sessionData.currentTenant,
    });
    
    // Increment data refresh trigger to cause all components to refetch data
    // This ensures that when tenant selection changes, all views automatically refetch
    // with the new tenant filter applied
    setDataRefreshTrigger(prev => {
      const newValue = prev + 1;
      logger.log(' [SESSION UPDATE] Incrementing dataRefreshTrigger to:', newValue);
      return newValue;
    });
    
    logger.log(' [SESSION UPDATE] Session updated successfully');
  }, []);

  const completeOidcRenewalSuccess = useCallback(async () => {
    renewalBackoffRef.current.errorCount = 0;
    renewalBackoffRef.current.nextRetryAt = 0;
    renewalBackoffRef.current.warned = false;
    if (session?.sessionId) {
      await refreshSessionData();
    }
  }, [session?.sessionId, refreshSessionData]);

  const handleTokenExpirationCleanup = useCallback(async () => {
    if (isSecurityDashboardPdfPrintBrowserContext()) {
      logger.log(' [TOKEN EXPIRATION] Skipping cleanup/redirect — security dashboard PDF print (headless) context')
      return
    }
    logger.log(' [TOKEN EXPIRATION] Resolving OIDC renewal failure (keep session if Aegis still valid)...');
    await resolveOidcRenewalFailure({
      sessionId: session?.sessionId,
      error: new Error('Your session has expired. Please log in again to continue.'),
      validateSession,
      clearAuthForReLogin,
    });
  }, [session?.sessionId, clearAuthForReLogin]);

  const handleTokenExpiration = useCallback(async () => {
    if (isSecurityDashboardPdfPrintBrowserContext()) {
      logger.log(' [TOKEN EXPIRATION] Skipping — security dashboard PDF print (headless) context')
      return
    }
    logger.log(' [TOKEN EXPIRATION] Handling expired access token...');

    try {
      if (userManager) {
        const renewedUser = await trySilentRenewAndApply(userManager, silentRenewInFlightRef, {
          setUser,
          setAuthToken,
        });
        if (renewedUser) {
          logger.log(' [TOKEN EXPIRATION] Silent renewal successful');
          await completeOidcRenewalSuccess();
          return;
        }
      }

      await handleTokenExpirationCleanup();
    } catch (error) {
      console.error('❌ [TOKEN EXPIRATION] Error handling token expiration:', error);
      await resolveOidcRenewalFailure({
        sessionId: session?.sessionId,
        error: error instanceof Error ? error : new Error('Your session has expired. Please log in again to continue.'),
        validateSession,
        clearAuthForReLogin,
      });
    }
  }, [
    userManager,
    session?.sessionId,
    setUser,
    setAuthToken,
    completeOidcRenewalSuccess,
    handleTokenExpirationCleanup,
    clearAuthForReLogin,
  ]);

  // Handle silent renewal errors
  const handleSilentRenewalError = useCallback(async (error: any) => {
    const errorMessage = error?.message ?? String(error ?? '');
    logger.log(' [SILENT RENEWAL ERROR] Handling silent renewal error:', errorMessage);

    try {
      if (errorMessage && (
        errorMessage.includes('X-Frame-Options') ||
        errorMessage.includes('frame') ||
        errorMessage.includes('iframe') ||
        errorMessage.includes('Refused to display') ||
        errorMessage.includes('frame-ancestors')
      )) {
        logger.log(' [SILENT RENEWAL ERROR] Iframe/X-Frame-Options error detected - this should be fixed by allowing SAMEORIGIN for silent-callback route');
        return;
      }

      if (errorMessage.includes('NetworkError') || errorMessage.includes('CORS')) {
        const now = Date.now();
        const { nextRetryAt } = renewalBackoffRef.current;
        if (now < nextRetryAt) {
          logger.log(' [SILENT RENEWAL ERROR] Backoff in effect, next retry at:', new Date(nextRetryAt).toISOString());
          return;
        }
        logger.log(' [SILENT RENEWAL ERROR] Network/CORS error detected, scheduling backoff retry...');
        scheduleSilentRenewRetry();
        return;
      }

      if (isRefreshTokenDeadError(error)) {
        logger.log(' [SILENT RENEWAL ERROR] Refresh token invalid, resolving failure without full sign-out...');
        await handleTokenExpirationCleanup();
        return;
      }

      // Keycloak prompt=none often returns login_required even when the user just authenticated
      // (and while createSession is still in flight). Do not treat that as session death.
      if (/login_required|interaction_required|consent_required|login required/i.test(errorMessage)) {
        logger.log(' [SILENT RENEWAL ERROR] IdP requires interaction; ignoring if Aegis session is viable');
        await handleTokenExpirationCleanup();
        return;
      }

      logger.log(' [SILENT RENEWAL ERROR] Unknown error, attempting final retry...');
      if (userManager) {
        const renewedUser = await trySilentRenewAndApply(userManager, silentRenewInFlightRef, {
          setUser,
          setAuthToken,
        });
        if (renewedUser) {
          logger.log(' [SILENT RENEWAL ERROR] Final retry successful');
          await completeOidcRenewalSuccess();
          return;
        }
      }

      await handleTokenExpirationCleanup();
    } catch (handlerError) {
      console.error('❌ [SILENT RENEWAL ERROR] Error in error handler:', handlerError);
      await handleTokenExpirationCleanup();
    }
  }, [
    userManager,
    setUser,
    setAuthToken,
    completeOidcRenewalSuccess,
    handleTokenExpirationCleanup,
    scheduleSilentRenewRetry,
  ]);

  useEffect(() => {
    handleTokenExpirationRef.current = handleTokenExpiration;
    handleSilentRenewalErrorRef.current = handleSilentRenewalError;
  }, [handleTokenExpiration, handleSilentRenewalError]);

  // Listen for token expiring soon events
  // Throttled available-tenants fetcher (used by initial hydration and WebSocket handler only)
  const allowAvailableTenantsFetchRef = useRef<boolean>(true);
  const firstDeliveryShownRef = useRef<boolean>(false);
  const remintSessionRef = useRef<() => Promise<void>>(async () => {
    throw new Error('remintSession not initialized yet');
  });
  const runFailsafeReLoginRef = useRef<() => void>(() => {
    logger.log(' [FAILSAFE] runFailsafeReLogin not initialized yet');
  });
  const fetchAvailableTenantsThrottled = useCallback(async (): Promise<TenantAccessDto[] | null> => {
    if (!allowAvailableTenantsFetchRef.current) return null;
    const now = Date.now();
    // Module-level refs to manage inflight and cooldown
    const inflightRef = (fetchAvailableTenantsThrottled as any)._inflight as Promise<TenantAccessDto[] | null> | null;
    const cooldownUntilRef = (fetchAvailableTenantsThrottled as any)._cooldownUntil as number | undefined;
    const cooldownUntil = cooldownUntilRef || 0;
    if (now < cooldownUntil) return null;
    if (inflightRef) return inflightRef;
    if (!session?.sessionId) return null;
    const authTokenData = localStorage.getItem('authToken');
    if (!authTokenData) return null;
    const { access_token } = JSON.parse(authTokenData);
    const { sessionsApi } = createApiInstances(access_token);
    const p = sessionsApi.getAvailableTenants1({ sessionId: session.sessionId })
      .then((resp: any) => {
        const list: TenantAccessDto[] = Array.isArray(resp?.data) ? resp.data : (resp?.availableTenants || resp || []);
        return Array.isArray(list) ? list : [];
      })
      .catch((e: any) => {
        try {
          const status = (e as any)?.response?.status;
          if (status === 429) {
            (fetchAvailableTenantsThrottled as any)._cooldownUntil = Date.now() + 5000;
          }
        } catch {}
        throw e;
      })
      .finally(() => { (fetchAvailableTenantsThrottled as any)._inflight = null; });
    (fetchAvailableTenantsThrottled as any)._inflight = p;
    return p;
  }, [session?.sessionId, createApiInstances]);
  
  // Token renewal cooldown to prevent loops
  const lastTokenRenewalRef = useRef<number>(0);
  const tokenRenewalInFlightRef = useRef<Promise<void> | null>(null);
  const TOKEN_RENEWAL_COOLDOWN = 30000; // 30 seconds

  useEffect(() => {
    const handleTokenExpiringSoon = async (event: CustomEvent) => {
      if (tokenRenewalInFlightRef.current) {
        logger.log(' [TOKEN EXPIRING SOON] Renewal already in flight — skipping duplicate request');
        return tokenRenewalInFlightRef.current;
      }

      const renewalPromise = (async () => {
        const now = Date.now();
        const timeSinceLastRenewal = now - lastTokenRenewalRef.current;
        const timeUntilExpiry =
          typeof event.detail?.timeUntilExpiry === 'number' ? event.detail.timeUntilExpiry : Infinity;
        const forceRenewal =
          event.detail?.forceRenewal === true || timeUntilExpiry <= 0;

        if (!forceRenewal && timeSinceLastRenewal < TOKEN_RENEWAL_COOLDOWN) {
          logger.log(` [TOKEN EXPIRING SOON] Skipping renewal - cooldown active (${Math.round((TOKEN_RENEWAL_COOLDOWN - timeSinceLastRenewal) / 1000)}s remaining)`);
          return;
        }

        if (forceRenewal && timeUntilExpiry <= 0) {
          logger.log(' [TOKEN EXPIRING SOON] Token already expired — bypassing renewal cooldown');
        }

        logger.log(' [TOKEN EXPIRING SOON] Event received:', event.detail);

        if (!userManager) {
          return;
        }

        try {
          logger.log(' [TOKEN EXPIRING SOON] Attempting proactive token renewal...');
          lastTokenRenewalRef.current = now;

          const renewedUser = await trySilentRenewAndApply(userManager, silentRenewInFlightRef, {
            setUser,
            setAuthToken,
          });
          if (renewedUser) {
            logger.log(' [TOKEN EXPIRING SOON] Proactive renewal successful');
            await completeOidcRenewalSuccess();
          } else {
            console.warn(' [TOKEN EXPIRING SOON] Proactive renewal failed - no usable access token');
            logger.log(' [TOKEN EXPIRING SOON] Resolving renewal failure without full sign-out');
            await handleTokenExpirationCleanup();
          }
        } catch (error) {
          console.warn(' [TOKEN EXPIRING SOON] Proactive renewal error:', error);
          logger.log(' [TOKEN EXPIRING SOON] Resolving renewal failure without full sign-out');
          await handleTokenExpirationCleanup();
        }
      })();

      tokenRenewalInFlightRef.current = renewalPromise;
      try {
        await renewalPromise;
      } finally {
        tokenRenewalInFlightRef.current = null;
      }
    };

    window.addEventListener('tokenExpiringSoon', handleTokenExpiringSoon as unknown as EventListener);

    return () => {
      window.removeEventListener('tokenExpiringSoon', handleTokenExpiringSoon as unknown as EventListener);
    };
  }, [userManager, setUser, setAuthToken, completeOidcRenewalSuccess, handleTokenExpirationCleanup]);

  // Create UserManager with dynamic auth discovery
  const createUserManagerWithDiscovery = useCallback(async (email?: string) => {
    // Log API request for auth discovery
    const requestId = `auth-discovery-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

    logger.log(' [AUTH DEBUG] createUserManagerWithDiscovery called with email:', email);
    logger.log(' [AUTH DEBUG] Request ID:', requestId);

    let authConfig: AuthConfig;
    
    try {
      // Direct API call for auth discovery
      const apiBaseURL = (process.env.NEXT_PUBLIC_API_BASE_URL as string) || '';
      const baseUrl = apiBaseURL.replace(/\/$/, '');
      const contextPath = getApiContextPath();
      const { apiBase } = normalizeApiBaseUrl(baseUrl, { contextPath });
      // SECURITY (pentest finding M04 / hardening): this used to fall back to the literal string
      // 'system.local' - the platform's special admin-tenant domain - when no window.location was
      // available (SSR). That magic string has no legitimate use here (discovery is inherently
      // tied to the browser's own hostname) and only served to leak the admin domain's name into
      // the client bundle / a real discover call against it. Auth discovery genuinely requires a
      // browser hostname; without one, fail fast instead of guessing a domain.
      if (typeof window === 'undefined' || !window.location?.hostname) {
        throw new Error(`Auth discovery unavailable: no browser hostname context (Request ID: ${requestId})`);
      }
      const domain = window.location.hostname;
      const response = await fetch(`${apiBase}/v1/auth/discover/${domain}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error(`Auth discovery failed: ${response.status} ${response.statusText}`);
      }
      
      authConfig = await response.json();
      logger.log(' [AUTH DEBUG] Retrieved auth config for domain:', domain);
    } catch (error) {
      console.error('❌ [AUTH DEBUG] Auth discovery failed:', error);
      
      // Create specific error types based on the error
      let enhancedError;
      if (error instanceof Error) {
        if (error.message.includes('Domain not found')) {
          enhancedError = new Error(`Endpoint mismatch: domain not found via /api/v1/auth/discover/{domain} (Request ID: ${requestId})`);
        } else {
          enhancedError = new Error(`Auth discovery failed: ${error.message} (Request ID: ${requestId})`);
        }
      } else {
        enhancedError = new Error(`Auth discovery failed: Unknown error (Request ID: ${requestId})`);
      }
      
      console.error('❌ [AUTH DEBUG] Enhanced error details:', enhancedError);
      
      // Fallback to static environment variables if auth discovery fails
      logger.log(' [AUTH DEBUG] Falling back to static environment variables');
      const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY;
      const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID;
      
      logger.log(' [AUTH DEBUG] Fallback config:', {
        authority,
        clientId,
        hasAuthority: !!authority,
        hasClientId: !!clientId
      });
      
      if (!authority || !clientId) {
        const fallbackError = new Error(`OIDC configuration missing: Both NEXT_PUBLIC_OIDC_AUTHORITY and NEXT_PUBLIC_OIDC_CLIENT_ID must be set (Request ID: ${requestId})`);
        console.error('❌ [AUTH DEBUG] Fallback config also missing:', fallbackError);
        throw fallbackError;
      }
      
      authConfig = {
        authority,
        client_id: clientId,
        provider_type: 'local'
      };
      logger.log(' [AUTH DEBUG] Using fallback auth config:', authConfig);
    }

    if (!authConfig || !authConfig.authority || !authConfig.client_id) {
      const configError = new Error(`Invalid auth configuration: Auth config missing required fields (Request ID: ${requestId})`);
      console.error('❌ [AUTH DEBUG] Invalid auth configuration:', configError);
      throw configError;
    }

    const settings = {
      authority: authConfig.authority,
      client_id: authConfig.client_id,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/auth/logout`,
      response_type: 'code',
      scope: 'openid profile email',
      automaticSilentRenew: false,
      silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      filterProtocolClaims: true,
      loadUserInfo: true,
      monitorSession: false,
      checkSessionInterval: 60,
      // Add these settings for better token management
      accessTokenExpiringNotificationTime: 60, // Notify 60 seconds before expiry
      includeIdTokenInSilentRenew: false, // Only renew access token
    };

    logger.log(' [AUTH DEBUG] Creating UserManager with dynamic settings:', {
      authority: settings.authority,
      client_id: settings.client_id,
      redirect_uri: settings.redirect_uri,
      provider_type: authConfig.provider_type,
      requestId
    });
    
    const userManager = new UserManager(settings);
    
    // Add event listeners for better debugging
    userManager.events.addUserLoaded((user) => {
      logger.log(' [OIDC] User loaded event (dynamic):', user.profile.email);
    });
    
    userManager.events.addUserUnloaded(() => {
      logger.log(' [OIDC] User unloaded event (dynamic)');
    });
    
    userManager.events.addSilentRenewError((error) => {
      console.error('❌ [OIDC] Silent renewal error (dynamic):', error);
      void handleSilentRenewalErrorRef.current(error);
    });
    
    userManager.events.addAccessTokenExpiring(() => {
      logger.log(' [OIDC] Access token expiring, attempting silent renewal (dynamic)...');
    });
    
    userManager.events.addAccessTokenExpired(() => {
      logger.log('❌ [OIDC] Access token expired (dynamic)');
      void handleTokenExpirationRef.current();
    });
    
    return userManager;
  }, []);

  // Create or update session - memoized to prevent recreations
  // `user` may be null on tenant switch after hard refresh (Aegis session + Bearer still valid).
  const createOrUpdateSession = useCallback(async (user: User | null, tenants: TenantAccessDto[], selectedTenantId: string): Promise<MultiTenantSessionDto> => {
    try {
      logger.log('AuthProviderClient: Creating user session for tenant:', selectedTenantId);
      
      // Prevent multiple session creations (ref so nested preferred-tenant switch after create works)
      if (sessionCreationInProgressRef.current) {
        logger.log('AuthProviderClient: Session creation already in progress, aborting duplicate createOrUpdateSession');
        throw new Error('Session creation already in progress');
      }
      
      sessionCreationInProgressRef.current = true;

      const isTenantSwitch = !!(session && session.sessionId);
      // Switch uses the live Aegis Bearer from storage; create still needs the OIDC access token.
      const jwtToken = isTenantSwitch
        ? (getAuthToken() || user?.access_token)
        : user?.access_token;
      if (!jwtToken) {
        throw new Error(
          isTenantSwitch
            ? 'No Aegis session token available for tenant switch'
            : 'No JWT token available from user object'
        );
      }
      if (!isTenantSwitch && !user) {
        throw new Error('User required to create a new session');
      }

      // Create API instances with auth token
      logger.log('AuthProviderClient: Creating API instances with JWT token:', jwtToken ? 'present' : 'missing');
      const { sessionsApi } = createApiInstances(jwtToken);
      logger.log('AuthProviderClient: API instances created successfully');

      // Only seed storage with the OIDC token when minting a new session. On switch, do not
      // overwrite the Aegis Bearer with a possibly-expired OIDC access_token.
      if (!isTenantSwitch && user?.access_token) {
        const authTokenData = {
          access_token: user.access_token,
          token_type: 'Bearer'
        };
        localStorage.setItem('authToken', JSON.stringify(authTokenData));
        logger.log('AuthProviderClient: JWT token stored for API authentication');
      }

      // Get email from oidc-client-ts OpenID Connect profile or restored Aegis session profile
      const email = (user?.profile?.email || session?.profile?.email) as string;
      
      let nextSession: MultiTenantSessionDto;

      if (isTenantSwitch) {
        // We have a current session; perform a switch-tenant
        logger.log('AuthProviderClient: Switching tenant via SessionsApi.switchTenant:', selectedTenantId);
        const switchReq: SwitchTenantRequest = { tenantId: selectedTenantId } as any;
        const switchResp: TenantSwitchResponse = await sessionsApi.switchTenant({
          sessionId: session!.sessionId!,
          switchTenantRequest: switchReq,
        });
        try {
          // Expose for debugging/inspection in the browser
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).__lastTenantSwitchResponse = switchResp;
          logger.log(' [AUTH SWITCH] Raw switch-tenant response JSON:',
            JSON.stringify(switchResp, null, 2));
        } catch (e) {
          console.warn(' [AUTH SWITCH] Failed to stringify switch-tenant response:', e);
          logger.log(' [AUTH SWITCH] Raw switch-tenant response (object):', switchResp);
        }
        if (!switchResp.success) {
          throw new Error(switchResp.error || 'Failed to switch tenant');
        }
        nextSession = switchResp.data!;
        // Log session deltas post-switch for verification
        const responseTenants = nextSession?.availableTenants || [];
        logger.log(' [AUTH SWITCH] nextSession.currentTenant:', nextSession?.currentTenant);
        logger.log(' [AUTH SWITCH] availableTenants (response): count=', responseTenants.length, 'ids=', responseTenants.map(t => t.tenantId));
        logger.log(' [AUTH SWITCH] availableTenants (previous): count=', tenants?.length || 0, 'ids=', (tenants || []).map(t => t.tenantId));
        if ((responseTenants?.length || 0) < (tenants?.length || 0)) {
          console.warn(' [AUTH SWITCH] Response availableTenants is smaller than previous known list. This may hide switch UI if not preserved.');
        }
        // FE sanity: verify backend actually switched to requested tenant
        const switchedId = nextSession?.currentTenant?.tenantId;
        if (!switchedId || switchedId !== selectedTenantId) {
          throw new Error(`Tenant switch mismatch: requested ${selectedTenantId}, got ${switchedId || 'none'}`);
        }
        logger.log('AuthProviderClient: Tenant switched successfully:', nextSession.currentTenant?.tenantId);
      } else {
        // No current session; create a new one bound to the selected tenant
        if (!user) {
          throw new Error('User required to create a new session');
        }
        // Create user profile for our application
        logger.log('AuthProviderClient: Creating user profile...');
        const userProfile: UserProfileDto = {
          userId: user.profile.sub || user.profile.preferred_username || email,
          email,
          name: user.profile.name || (user.profile.given_name && user.profile.family_name ? 
            `${user.profile.given_name} ${user.profile.family_name}` : 
            user.profile.given_name || user.profile.family_name || email),
          givenName: user.profile.given_name,
          familyName: user.profile.family_name,
          picture: user.profile.picture,
          preferredUsername: user.profile.preferred_username,
        };

        // Create session request using generated API types
        const sessionRequest: CreateSessionRequest = {
          userProfile,
          currentTenantId: selectedTenantId,
          metadata: {
            user_agent: navigator.userAgent,
            login_method: 'oidc',
          },
        };

        logger.log('AuthProviderClient: Calling sessionsApi.createSession with selectedTenantId:', selectedTenantId);
        logger.log('AuthProviderClient: Session request payload:', JSON.stringify(sessionRequest, null, 2));
        
        try {
          const sessionResponse: SessionResponse = await sessionsApi.createSession({
            createSessionRequest: sessionRequest
          });
          logger.log('AuthProviderClient: Session creation API response:', sessionResponse);
          
          if (!sessionResponse.success) {
            console.error('AuthProviderClient: Session creation failed:', sessionResponse.error);
            throw new Error(sessionResponse.error || 'Failed to create session');
          }
          nextSession = sessionResponse.data!;
          // Reset failure counters on successful session creation
          resetAuthFailureCounts();
          logger.log('AuthProviderClient: Session created successfully with tenant info:', nextSession);
          persistSessionToStorage({
            session: nextSession,
            availableTenants: nextSession.availableTenants,
            currentTenant: nextSession.currentTenant,
          });
        } catch (apiError) {
          console.error('AuthProviderClient: Session creation API call failed:', apiError);
          throw apiError;
        }
      }

      // SECURITY (pentest finding C01 core fix): from this point on, every API call must use the
      // Aegis-signed session access token that was just minted/re-minted - NOT the OIDC token used
      // only to reach this endpoint. Swap it into storage immediately so csrf-enabled-client.ts's
      // live token getter and lib/api/auth-headers.ts's raw-fetch helper both pick it up right away.
      if (nextSession.accessToken) {
        setAuthToken({ access_token: nextSession.accessToken, token_type: 'Bearer' });
      } else {
        logger.warn('AuthProviderClient: Session response did not include an accessToken - Aegis will reject subsequent requests once the OIDC token stops being accepted for non-mint endpoints');
      }

      setSession(nextSession);
      
      // Extract available tenants from session response, with fallback to previous known list
      const responseTenants = nextSession.availableTenants || [];
      const updatedTenants = Array.isArray(responseTenants) && responseTenants.length > 0
        ? responseTenants
        : tenants;
      logger.log('AuthProviderClient: Available tenants (post-switch):', updatedTenants);
      // Merge existing tenants with response to avoid UI regressions hiding the switcher
      const mergedTenantsMap = new Map<string, TenantAccessDto>();
      for (const t of tenants || []) {
        if (t?.tenantId) mergedTenantsMap.set(t.tenantId, t);
      }
      for (const t of responseTenants || []) {
        if (t?.tenantId) mergedTenantsMap.set(t.tenantId, t);
      }
      const mergedTenants = Array.from(mergedTenantsMap.values());
      logger.log('AuthProviderClient: Available tenants (merged):', mergedTenants);
      setAvailableTenants(mergedTenants);
      persistSessionToStorage({
        session: nextSession,
        availableTenants: mergedTenants,
        currentTenant: nextSession.currentTenant,
      });

      // Check mergedTenants (the computed value) not availableTenants (state that hasn't updated yet)
      if (mergedTenants.length === 0) {
        logger.error('AuthProviderClient: User is not authorized for any tenants');
        const noTenantError = new Error('You are not authorized for any tenants. Please contact your administrator.');
        clearAuthForReLogin(noTenantError);
        setUser(null);
        throw noTenantError;
      }

      // Set current tenant from session
      if (nextSession.currentTenant) {
        setCurrentTenant(nextSession.currentTenant);
        logger.log('AuthProviderClient: Current tenant set from session:', nextSession.currentTenant.tenantId);
      }

      // Store the selected tenant as preferred
      if (nextSession.currentTenant) {
        localStorage.setItem('preferred_tenant_id', nextSession.currentTenant.tenantId!);
        logger.log('AuthProviderClient: Stored preferred tenant:', nextSession.currentTenant.tenantId);
      }

      // Since we just created a session with a specific tenant, proceed to the app
      logger.log('AuthProviderClient: Session created with selected tenant, proceeding to app');
      setNeedsTenantSelection(false);
      setNeedsEmailEntry(false);
      setError(null);
      setIsLoading(false);
      
      // Trigger data refresh for all components
      setDataRefreshTrigger(prev => {
        logger.log(' [AUTH] Triggering data refresh (selectTenant), new trigger value:', prev + 1);
        return prev + 1;
      });
      
      // Fire a global event so other parts of the app can react immediately (e.g., show Switch Tenant button)
      try { window.dispatchEvent(new Event('tenantBindingsChanged')); } catch {}

      // Clear any pending debounced requests since we just completed one
      if (sessionCreationTimerRef.current) {
        clearTimeout(sessionCreationTimerRef.current);
        sessionCreationTimerRef.current = null;
      }
      pendingSessionRequestRef.current = null;
      
      return nextSession;
    } catch (err) {
      console.error('AuthProviderClient: Error creating session:', err);
      setError(err instanceof Error ? err : new Error('Failed to create session'));
      setIsLoading(false);
      throw err;
    } finally {
      sessionCreationInProgressRef.current = false;
    }
  }, [createApiInstances, session, clearAuthForReLogin, setAuthToken, getAuthToken]);

  // Create UserManager with dynamic OIDC configuration from backend
  // This allows us to use oidc-client-ts fully with domain-specific configs
  // Moved before callback handler to fix dependency order
  const createUserManagerForOidcConfig = useCallback((oidcConfig: OidcConfig) => {
    if (!oidcConfig?.issuer || !oidcConfig?.clientId) {
      logger.log(' [AUTH DEBUG] Missing OIDC configuration:', { oidcConfig });
      return null;
    }

    // Normalize issuer URL to ensure exact matching
    // Remove trailing slashes and normalize the URL
    const normalizedIssuer = oidcConfig.issuer.trim().replace(/\/+$/, '');
    const normalizedRedirectUri = `${window.location.origin}/auth/callback`;

    const settings = {
      authority: normalizedIssuer,
      client_id: oidcConfig.clientId,
      redirect_uri: normalizedRedirectUri,
      post_logout_redirect_uri: `${window.location.origin}/auth/logout`,
      response_type: 'code',
      scope: 'openid profile email',
      automaticSilentRenew: false,
      silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      filterProtocolClaims: true,
      loadUserInfo: true,
      monitorSession: false,
      checkSessionInterval: 60,
      accessTokenExpiringNotificationTime: 60,
      includeIdTokenInSilentRenew: false,
      // Enable PKCE (required by Keycloak) - library handles this automatically
      code_challenge_method: 'S256',
    };

    logger.log(' [AUTH DEBUG] UserManager settings (normalized):', {
      authority: settings.authority,
      client_id: settings.client_id,
      redirect_uri: settings.redirect_uri,
      originalIssuer: oidcConfig.issuer
    });

    logger.log(' [AUTH DEBUG] Creating UserManager with OIDC config:', {
      issuer: oidcConfig.issuer,
      clientId: oidcConfig.clientId
    });

    const userManager = new UserManager(settings);
    
    // Add event listeners
    userManager.events.addUserLoaded((user) => {
      logger.log(' [OIDC] User loaded event:', user.profile.email);
    });
    
    userManager.events.addUserUnloaded(() => {
      logger.log(' [OIDC] User unloaded event');
    });
    
    userManager.events.addSilentRenewError((error) => {
      console.error('❌ [OIDC] Silent renewal error:', error);
      void handleSilentRenewalErrorRef.current(error);
    });
    
    userManager.events.addAccessTokenExpiring(() => {
      logger.log(' [OIDC] Access token expiring, attempting silent renewal...');
    });
    
    userManager.events.addAccessTokenExpired(() => {
      logger.log('❌ [OIDC] Access token expired');
      void handleTokenExpirationRef.current();
    });

    return userManager;
  }, []);

  /**
   * Restore OIDC UserManager + user after hard refresh.
   * Prefer the same authority/client used at login (persisted); fall back to env defaults.
   * If the stored OIDC access token is expired, try silent renew once — do not clear Aegis session on failure.
   */
  const tryRestoreOidcUser = useCallback(async (): Promise<boolean> => {
    const tryWithManager = async (um: UserManager, label: string): Promise<boolean> => {
      try {
        let existingUser = await um.getUser();
        if (existingUser && existingUser.expired) {
          logger.log(` [SESSION DEBUG] OIDC user expired (${label}) — attempting silent renew`);
          try {
            existingUser = await um.signinSilent();
          } catch (renewErr) {
            logger.log(` [SESSION DEBUG] Silent renew failed (${label}); continuing with Aegis session only`, renewErr);
            existingUser = null;
          }
        }
        if (existingUser && !existingUser.expired) {
          logger.log(` [SESSION DEBUG] OIDC user restored (${label})`);
          setUserManager(um);
          setUser(existingUser);
          return true;
        }
      } catch (e) {
        console.warn(` [SESSION DEBUG] OIDC restore failed (${label}):`, e);
      }
      return false;
    };

    const persisted = readOidcConfigForRestore();
    if (persisted) {
      const um = createUserManagerForOidcConfig({
        issuer: persisted.issuer,
        clientId: persisted.clientId,
        tokenEndpointPath: persisted.tokenEndpointPath,
      } as OidcConfig);
      if (um && (await tryWithManager(um, 'persisted-oidc-config'))) {
        return true;
      }
    }

    try {
      const pendingRaw = localStorage.getItem('pendingDomainConfig');
      if (pendingRaw) {
        const pending = JSON.parse(pendingRaw) as LoginDomainConfig;
        const cfg = pending?.oidcConfig as OidcConfig | undefined;
        if (cfg?.issuer && cfg?.clientId) {
          const um = createUserManagerForOidcConfig(cfg);
          if (um && (await tryWithManager(um, 'pendingDomainConfig'))) {
            saveOidcConfigForRestore(cfg);
            return true;
          }
        }
      }
    } catch {
      /* ignore */
    }

    try {
      const tempUserManager = createUserManager();
      return await tryWithManager(tempUserManager, 'env-default');
    } catch (e) {
      console.warn(' [SESSION DEBUG] Could not restore OIDC user via env defaults:', e);
      return false;
    }
  }, [createUserManager, createUserManagerForOidcConfig]);

  // Handle callback processing using oidc-client-ts signinRedirectCallback
  useEffect(() => {
    const processCallback = async () => {
      if (pathname !== '/auth/callback' || user) return;

      // Clear stale Aegis session only — must preserve OIDC PKCE/state for signinRedirectCallback
      clearStaleClientSessionForLogin({ preserveOidcHandshake: true });
      
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      
      // Check if we have the required OAuth parameters
      if (!code || !state) {
        logger.log('AuthProviderClient: Missing code or state in callback URL');
        if (!code && !state) {
          // No OAuth parameters - might be a direct navigation, ignore
          return;
        }
        setError(new Error('Invalid callback parameters. Please try logging in again.'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        // Get OIDC config from sessionStorage (stored during redirect)
        const oidcConfigStr = sessionStorage.getItem('oidc_config_for_callback');
        let oidcConfig: OidcConfig | null = null;

        if (oidcConfigStr) {
          try {
            const parsed = JSON.parse(oidcConfigStr);
            oidcConfig = {
              issuer: parsed.issuer,
              clientId: parsed.clientId,
              tokenEndpointPath: parsed.tokenEndpointPath
            };
            saveOidcConfigForRestore(oidcConfig);
          } catch (e) {
            logger.log('AuthProviderClient: Failed to parse stored OIDC config', e);
          }
        }

        // Fallback to environment variables if config not stored
        if (!oidcConfig) {
          const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY || '';
          const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID || '';
          
          if (!authority || !clientId) {
            setError(new Error('OIDC configuration missing. Please try logging in again.'));
            setIsLoading(false);
            return;
          }
          
          oidcConfig = {
            issuer: authority,
            clientId
          };
        }

        // Use the SAME createUserManagerForOidcConfig function used during redirect
        // This ensures the UserManager settings match exactly, so state can be found in localStorage
        if (!oidcConfig.issuer || !oidcConfig.clientId) {
          throw new Error('Invalid OIDC configuration');
        }
        
        logger.log('AuthProviderClient: Creating UserManager with stored OIDC config for callback', {
          issuer: oidcConfig.issuer,
          clientId: oidcConfig.clientId,
          redirect_uri: `${window.location.origin}/auth/callback`
        });

        // CRITICAL: Clear old session and OIDC user data BEFORE creating UserManager
        // The UserManager reads from localStorage when created, so we must clear old data first
        logger.log(' [CALLBACK] Clearing old session and OIDC user data before processing callback...');

        setPendingDomainConfig(null);
        // Get all localStorage keys first
        const allLocalStorageKeys = Object.keys(localStorage);
        
        localStorage.removeItem('pendingDomainConfig');
        localStorage.removeItem('availableTenantsVersion');
        
        // Clear all auth_choice keys to prevent using old user's auth choice
        allLocalStorageKeys.forEach(key => {
          if (key.startsWith('auth_choice_')) {
            logger.log(' [CALLBACK] Removing old auth_choice key:', key);
            localStorage.removeItem(key);
          }
        });
        
        // CRITICAL: Clear ALL OIDC user data before creating UserManager
        // The oidc-client-ts library stores user data with keys like: 
        // oidc.user:https://auth.cyberwellsolutions.io/realms/cyberwell:cyberwell-client-app
        // We MUST clear this before creating UserManager, otherwise it will restore the old user
        // The WebStorageStateStore reads from localStorage when UserManager is created
        const oidcUserKeys: string[] = [];
        allLocalStorageKeys.forEach(key => {
          // Check for OIDC user data keys (exact match or contains pattern)
          // Format: oidc.user:{authority}:{client_id}
          // This key contains: id_token, access_token, refresh_token, profile (from old user)
          if (key.includes('oidc.user:') || (key.startsWith('oidc.') && key.includes('user'))) {
            logger.log(' [CALLBACK] Removing old OIDC user data:', key);
            localStorage.removeItem(key);
            oidcUserKeys.push(key);
          }
        });
        
        if (oidcUserKeys.length > 0) {
          logger.log(' [CALLBACK] Cleared OIDC user keys before creating UserManager:', oidcUserKeys);
        } else {
          logger.log(' [CALLBACK] No OIDC user keys found to clear');
        }
        
        // Log remaining localStorage keys for debugging
        const remainingKeys = Object.keys(localStorage);
        const remainingOidcKeys = remainingKeys.filter(k => k.includes('oidc'));
        if (remainingOidcKeys.length > 0) {
          logger.log(' [CALLBACK] Remaining OIDC keys in localStorage:', remainingOidcKeys);
        }

        // Debug: Log all localStorage keys to see what state keys exist
        const oidcLocalStorageKeys = Object.keys(localStorage).filter(key => key.startsWith('oidc.'));
        logger.log(' [AUTH DEBUG] localStorage keys in callback:', oidcLocalStorageKeys);
        
        // Log the actual localStorage values for debugging
        allLocalStorageKeys.forEach(key => {
          try {
            const value = localStorage.getItem(key);
            if (value) {
              const parsed = JSON.parse(value);
              logger.log(` [AUTH DEBUG] localStorage[${key}]:`, {
                hasState: !!parsed.state,
                hasCodeVerifier: !!parsed.code_verifier,
                stateLength: parsed.state?.length,
                keys: Object.keys(parsed)
              });
            }
          } catch (e) {
            const value = localStorage.getItem(key);
            logger.log(` [AUTH DEBUG] localStorage[${key}]: (not JSON)`, value ? value.substring(0, 100) : 'null');
          }
        });
        
        // Use the same function that was used during redirect to ensure settings match exactly
        const userManager = createUserManagerForOidcConfig(oidcConfig);
        if (!userManager) {
          throw new Error('Failed to create UserManager with OIDC configuration');
        }

        // CRITICAL: Remove any existing user from UserManager before processing callback
        // This ensures we don't use old user data that might still be in memory or localStorage
        try {
          logger.log(' [CALLBACK] Removing any existing user from UserManager...');
          
          // First, check if there's a user in localStorage (UserManager reads from it)
          const checkKeys = Object.keys(localStorage);
          const existingOidcUserKey = checkKeys.find(key => 
            key.includes('oidc.user:') || (key.startsWith('oidc.') && key.includes('user'))
          );
          
          if (existingOidcUserKey) {
            logger.log(' [CALLBACK] Found OIDC user data in localStorage, removing:', existingOidcUserKey);
            localStorage.removeItem(existingOidcUserKey);
          }
          
          // Now remove from UserManager (this also clears localStorage)
          const existingUser = await userManager.getUser();
          if (existingUser) {
            logger.log(' [CALLBACK] Found existing user in UserManager, removing:', existingUser.profile.email);
            await userManager.removeUser().catch(() => {});
          }
          await userManager.clearStaleState().catch(() => {});
          
          // Final check: Clear OIDC user data from localStorage one more time after UserManager operations
          // Sometimes UserManager operations can restore data, so we clear again
          const finalKeys = Object.keys(localStorage);
          finalKeys.forEach(key => {
            if (key.includes('oidc.user:') || (key.startsWith('oidc.') && key.includes('user'))) {
              logger.log(' [CALLBACK] Final cleanup - removing OIDC user data:', key);
              localStorage.removeItem(key);
            }
          });
          
          logger.log(' [CALLBACK] UserManager cleared of old user data');
        } catch (e) {
          logger.log(' [CALLBACK] Error clearing UserManager (non-fatal):', e);
        }

        // CRITICAL: One final check - clear any OIDC user data right before callback
        // Sometimes the UserManager operations can restore data, so we clear one more time
        // We need to clear ALL OIDC user data to ensure signinRedirectCallback processes the new callback code
        // The signinRedirectCallback() should use the CODE from the URL, not cached user data
        const finalCheckKeys = Object.keys(localStorage);
        const clearedOidcUserKeys: string[] = [];
        finalCheckKeys.forEach(key => {
          // Clear any OIDC user data (format: oidc.user:{authority}:{client_id})
          // This contains id_token, access_token, refresh_token, profile from old user
          // We must NOT clear state/nonce/code_verifier as those are needed for callback validation
          if (key.includes('oidc.user:') || (key.startsWith('oidc.') && key.includes('user') && !key.includes('state') && !key.includes('nonce') && !key.includes('code_verifier'))) {
            logger.log(' [CALLBACK] Final cleanup - removing OIDC user data:', key);
            localStorage.removeItem(key);
            clearedOidcUserKeys.push(key);
          }
        });
        
        if (clearedOidcUserKeys.length > 0) {
          logger.log(' [CALLBACK] Cleared OIDC user keys before callback:', clearedOidcUserKeys);
        }
        
        // Verify no OIDC user data remains
        const verifyKeys = Object.keys(localStorage);
        const remainingOidcUserKeys = verifyKeys.filter(k => 
          k.includes('oidc.user:') || (k.startsWith('oidc.') && k.includes('user') && !k.includes('state') && !k.includes('nonce'))
        );
        if (remainingOidcUserKeys.length > 0) {
          logger.warn(' [CALLBACK] WARNING: OIDC user data still exists after cleanup:', remainingOidcUserKeys);
          // Force clear them
          remainingOidcUserKeys.forEach(k => {
            logger.log(' [CALLBACK] Force removing remaining OIDC user data:', k);
            localStorage.removeItem(k);
          });
        }

        logger.log('AuthProviderClient: Using oidc-client-ts signinRedirectCallback', {
          stateFromUrl: state,
          codeFromUrl: code ? 'present' : 'missing',
          clearedOidcUserKeys: clearedOidcUserKeys.length
        });
        
        // Use the library's callback handler - it handles:
        // - State validation (will find state in localStorage because settings match)
        // - PKCE code verifier validation
        // - Token exchange using the CODE from the URL (not old user data)
        // - User object creation from the NEW tokens
        // CRITICAL: This will create a NEW user from the callback code in the URL, not restore old user
        // The callback code in the URL will be used to exchange for new tokens for the NEW user
        const callbackUser = await userManager.signinRedirectCallback();
        
        // Verify the callback user is actually the new user (not the old one)
        logger.log(' [CALLBACK] Callback user received:', {
          email: callbackUser.profile.email,
          sub: callbackUser.profile.sub,
          hasIdToken: !!callbackUser.id_token,
          hasAccessToken: !!callbackUser.access_token
        });
        
        logger.log('AuthProviderClient: Callback processed successfully', {
          email: callbackUser.profile.email
        });

        // Set the UserManager and user; session creation is triggered by the single "Process user when loaded" effect (ensureSessionForUser)
        setUserManager(userManager);
        setUser(callbackUser);

        const restoredLoginDomainConfig = readLoginDomainConfigFromSessionStorage();
        if (restoredLoginDomainConfig) {
          setPendingDomainConfig(restoredLoginDomainConfig);
          try {
            localStorage.setItem(
              'pendingDomainConfig',
              JSON.stringify(restoredLoginDomainConfig)
            );
          } catch {
            /* ignore */
          }
          logger.log(' [CALLBACK] Restored login domain config for session create:', restoredLoginDomainConfig);
        }

        // Clean up stored OIDC config (login domain config kept until session is created)
        sessionStorage.removeItem('oidc_config_for_callback');
        
        // handleUserLoaded will have set isLoading to false by now
        // Only set it false here if handleUserLoaded didn't (shouldn't happen, but safety check)
        // setIsLoading(false); // This should already be false from handleUserLoaded
        
      } catch (err) {
        console.error('AuthProviderClient: Callback processing failed:', err);
        setError(err instanceof Error ? err : new Error('Failed to process authentication callback'));
        setIsLoading(false);
        
        // Clean up stored config
        sessionStorage.removeItem('oidc_config_for_callback');
        
        // Redirect back to login after a delay
        setTimeout(() => {
          setNeedsEmailEntry(true);
          setError(null);
          window.history.replaceState({}, '', '/');
        }, 3000);
      }
    };
    
    processCallback();
  }, [pathname, user, createUserManagerForOidcConfig, clearStaleClientSessionForLogin]);

  // FIXED: Single session restoration effect that only runs once
  useEffect(() => {
    // Playwright E2E: localStorage bootstrap in useLayoutEffect owns session state; skip OIDC restore race.
    if (isE2eSkipSessionMode()) {
      if (!sessionRestorationCompleted) {
        setSessionRestorationCompleted(true);
      }
      return;
    }

    // Skip if we're in callback or logout routes, already have user/session, or restoration already completed
    if (pathname === '/auth/callback' || 
        pathname === '/auth/silent-callback' ||
        pathname === '/auth/logout' ||
        sessionRestorationCompleted) {
      logger.log('AuthProviderClient: Skipping session restoration - already authenticated or in callback or completed');
      return;
    }

    // CRITICAL FIX: Don't skip if we have user/session - we need to validate they have proper permissions
    const hasCompleteSession = session && 
    session.sessionId && 
    session.currentTenant && 
    (session.currentTenantPermissions || session.effectivePermissions || session.currentTenant.permissions);

    if (hasCompleteSession) {
      logger.log('AuthProviderClient: Skipping session restoration - complete session with permissions exists');
      return;
    }

    // If we have a session but it's incomplete (missing permissions), we need to restore/refresh it
    if (session && !hasCompleteSession) {
      logger.log(' [SESSION DEBUG] Found incomplete session - missing permissions, will restore');
    }

    // Additional validation - if we have a session but it's clearly broken, clear it (use clearAuthForReLogin so all flags reset)
    if (session && session.sessionId && !session.currentTenant) {
      logger.log(' [SESSION DEBUG] Found broken session - has sessionId but no currentTenant, clearing');
      clearAuthForReLogin();
      // Continue with restoration process
    }

    if (user && !session) {
      logger.log(' [SESSION DEBUG] Found user without session - will create session');
    }

    logger.log(' [SESSION DEBUG] Starting session restoration check...');
    logger.log(' [SESSION DEBUG] Current pathname:', pathname);
    logger.log(' [SESSION DEBUG] Current state:', {
      hasUser: !!user,
      hasSession: !!session,
      hasSessionId: !!session?.sessionId,
      hasCurrentTenant: !!session?.currentTenant,
      hasPermissions: !!(session?.currentTenantPermissions || session?.effectivePermissions || session?.currentTenant?.permissions),
      sessionRestorationCompleted
    });
    
    const restoreSession = async () => {
      // Aegis Bearer lives in sessionStorage (persistAccessToken/setAuthToken); localStorage is legacy only.
      const authTokenData = readStoredAuthTokenJson();
      const storedSession = localStorage.getItem('session');
      const storedTenants = localStorage.getItem('availableTenants');
      const storedCurrentTenant = localStorage.getItem('currentTenant');
      
      // Validate that stored data is not just empty strings
      const hasValidAuthToken = !!(authTokenData && authTokenData.trim().length > 0);
      const hasValidSession = !!(storedSession && storedSession.trim().length > 0);
      
      logger.log(' [SESSION DEBUG] storage contents:', {
        hasAuthToken: !!authTokenData,
        authTokenInSessionStorage: !!sessionStorage.getItem('authToken'),
        authTokenInLocalStorage: !!localStorage.getItem('authToken'),
        hasStoredSession: !!storedSession,
        hasStoredTenants: !!storedTenants,
        hasStoredCurrentTenant: !!storedCurrentTenant,
        authTokenLength: authTokenData?.length || 0,
        sessionLength: storedSession?.length || 0,
        hasValidAuthToken,
        hasValidSession
      });
      // Prevent session restoration if logout is in progress
      if (logoutInProgress) {
        logger.log(' [SESSION DEBUG] Logout in progress, skipping session restoration');
        return;
      }
      
      // AGGRESSIVE CHECK: If localStorage is empty, don't even try to restore
      if (typeof window !== 'undefined' && window.localStorage.length === 0) {
        logger.log(' [SESSION DEBUG] localStorage is empty, skipping session restoration');
        setNeedsEmailEntry(true);
        setIsLoading(false);
        setSessionRestorationCompleted(true);
        return;
      }
      
      // Check for logout flag in sessionStorage
      const logoutFlag = sessionStorage.getItem('logout_in_progress');
      if (logoutFlag === 'true') {
        logger.log(' [SESSION DEBUG] Logout flag detected, skipping session restoration');
        sessionStorage.removeItem('logout_in_progress'); // Clean up the flag
        setNeedsEmailEntry(true);
        setIsLoading(false);
        setSessionRestorationCompleted(true);
        return;
      }

      // Do not run restoration on callback route: we are in the middle of login and have cleared
      // or are about to clear storage. Validating old session here would use stale sessionId and
      // get "Session has expired", and would race with the new session creation.
      if (pathname === '/auth/callback' || pathname === '/auth/silent-callback' || pathname === '/auth/logout') {
        logger.log(' [SESSION DEBUG] On auth callback/logout route, skipping session restoration');
        setSessionRestorationCompleted(true);
        return;
      }
      
      setIsLoading(true);

      try {
        // Step 1: Check for stored auth token and session first
        // Use validated checks to ensure data is not just empty strings
        if (hasValidAuthToken && hasValidSession) {
          logger.log(' [SESSION DEBUG] Found stored auth and session data, proceeding with restoration...');

          const isE2eMarketplace =
            typeof window !== 'undefined' &&
            localStorage.getItem('e2e_skip_session_websocket') === '1';

          if (isE2eMarketplace) {
            try {
              const parsedSession = JSON.parse(storedSession!);
              const parsedTenants = storedTenants
                ? JSON.parse(storedTenants)
                : parsedSession.availableTenants || [];
              const parsedCurrentTenant = storedCurrentTenant
                ? JSON.parse(storedCurrentTenant)
                : parsedSession.currentTenant;
              setSession(parsedSession);
              markSessionValidationStartNow();
              setAvailableTenants(parsedTenants);
              resolvePreferredTenantId(parsedTenants || []);
              setCurrentTenant(reconcileCurrentTenantWithSession(parsedSession, parsedCurrentTenant));
              setNeedsEnrollment(false);
              setNeedsTenantSelection(false);
              setNeedsEmailEntry(false);
              setIsLoading(false);
              setSessionRestorationCompleted(true);
              logger.log(' [SESSION DEBUG] E2E marketplace fast restore from localStorage');
              return;
            } catch (e2eRestoreError) {
              logger.log(' [SESSION DEBUG] E2E fast restore failed, falling back:', e2eRestoreError);
            }
          }
          
          try {
            let access_token: string | undefined;
            try {
              const parsedAuthToken = JSON.parse(authTokenData!);
              access_token = parsedAuthToken.access_token;
            } catch (parseError) {
              logger.log(' [SESSION DEBUG] Failed to parse authToken, might be malformed:', parseError);
              // Clear invalid auth token from both stores (sessionStorage is preferred)
              try {
                sessionStorage.removeItem('authToken');
              } catch { /* ignore */ }
              localStorage.removeItem('authToken');
              throw new Error('Invalid authToken format');
            }
            
            let parsedSession: any;
            try {
              parsedSession = JSON.parse(storedSession!);
            } catch (parseError) {
              logger.log(' [SESSION DEBUG] Failed to parse session, might be malformed:', parseError);
              // Clear invalid session
              localStorage.removeItem('session');
              throw new Error('Invalid session format');
            }
            const parsedTenants = storedTenants ? JSON.parse(storedTenants) : [];
            const parsedCurrentTenant = storedCurrentTenant ? JSON.parse(storedCurrentTenant) : null;
            
            logger.log(' [SESSION DEBUG] Restoration data:', {
              hasAccessToken: !!access_token,
              sessionId: parsedSession.sessionId,
              tenantsCount: parsedTenants.length,
              currentTenantId: parsedCurrentTenant?.tenantId
            });

            // CRITICAL FIX: Always validate session with backend if permissions are missing
            const hasStoredPermissions = !!(
              parsedSession.currentTenantPermissions || 
              parsedSession.effectivePermissions || 
              parsedCurrentTenant?.permissions
            );

            logger.log(' [SESSION DEBUG] Restoration data:', {
              hasAccessToken: !!access_token,
              sessionId: parsedSession.sessionId,
              tenantsCount: parsedTenants.length,
              currentTenantId: parsedCurrentTenant?.tenantId,
              hasStoredPermissions: hasStoredPermissions
            });
            
            if (!hasStoredPermissions) {
              logger.log(' [SESSION DEBUG] Session missing permissions - forcing backend validation');
            }
            
            // Check if session is recent (less than 5 minutes old) AND has permissions
            const sessionAge = Date.now() - new Date(parsedSession.createdAt || parsedSession.updatedAt).getTime();
            const isRecentSession = sessionAge < 5 * 60 * 1000; // 5 minutes
            
            logger.log(' [SESSION DEBUG] Session age check:', {
              sessionAge: Math.round(sessionAge / 1000),
              isRecent: isRecentSession,
              hasPermissions: hasStoredPermissions
            });
            
            // FIXED: Only skip backend validation if session is recent AND has permissions
            if (isRecentSession && hasStoredPermissions) {
              const validationResult = await validateSession(parsedSession.sessionId);
              if (!validationResult.isValid) {
                logger.log(' [SESSION DEBUG] Cached session failed backend validation — full sign-out');
                try {
                  sessionStorage.setItem('force_prompt_login', 'true');
                } catch { /* ignore */ }
                triggerFullSignOut(
                  validationResult.reason === 'expired' ? 'expired' : 'invalid',
                  new Error(validationResult.message || 'Your session is no longer valid. Please log in again.'),
                  { skipIdpRedirect: false }
                );
                return;
              }

              logger.log(' [SESSION DEBUG] Session is recent, has permissions, and validated — restoring');
              
              // Restore all session state immediately
              setSession(parsedSession);
              markSessionValidationStartNow();
              setAvailableTenants(parsedTenants);
              resolvePreferredTenantId(
                (parsedSession.availableTenants?.length ? parsedSession.availableTenants : parsedTenants) || []
              );
              const reconciledTenant = reconcileCurrentTenantWithSession(parsedSession, parsedCurrentTenant);
              setCurrentTenant(reconciledTenant);
              
              // If we have a sessionId and currentTenant, it's a valid session
              const hasValidSession = parsedSession.sessionId && reconciledTenant && reconciledTenant.tenantId;

              logger.log(' [SESSION DEBUG] Session type analysis:', {
                hasSessionId: !!parsedSession.sessionId,
                hasCurrentTenant: !!reconciledTenant?.tenantId,
                hasValidSession,
                sessionId: parsedSession.sessionId,
                currentTenantId: reconciledTenant?.tenantId || 'none'
              });

              if (hasValidSession) {
                logger.log(' [SESSION DEBUG] VALID TENANT SESSION - User should go to app');
                
                // Use tenants from session if available, otherwise from localStorage
                const tenantsToUse = (parsedSession.availableTenants && parsedSession.availableTenants.length > 0) 
                  ? parsedSession.availableTenants 
                  : parsedTenants;
                
                setAvailableTenants(tenantsToUse);
                setNeedsEnrollment(false);
                setNeedsTenantSelection(false);
                setNeedsEmailEntry(false);
                setError(null);
              } else {
                logger.log(' [SESSION DEBUG] INVALID SESSION - User needs enrollment');
                setNeedsEnrollment(true);
                setNeedsTenantSelection(false);
                setNeedsEmailEntry(false);
              }
              
              // Try to restore OIDC user (but not during logout or after logout)
              const logoutCompleted = sessionStorage.getItem('logout_completed') === 'true';
              if (!logoutInProgress && !logoutCompleted) {
                await tryRestoreOidcUser();
              } else {
                if (logoutInProgress) {
                  logger.log(' [SESSION DEBUG] Skipping OIDC user restoration during logout');
                }
                if (logoutCompleted) {
                  logger.log(' [SESSION DEBUG] Skipping OIDC user restoration after logout completion');
                }
              }
              
              setIsLoading(false);
              setSessionRestorationCompleted(true);
              logger.log(' [SESSION DEBUG] Session restoration completed');
              return;
            }
            
            // For sessions without permissions OR older sessions, validate with backend
            logger.log(' [SESSION DEBUG] Session needs backend validation - missing permissions or too old');
            
            // CRITICAL FIX: Use validateSession instead of getSession to properly detect expired sessions
            try {
              const validationResult = await validateSession(parsedSession.sessionId);
              
              if (!validationResult.isValid && validationResult.reason === 'expired') {
                logger.log(' [SESSION DEBUG] Session expired - clearing data and forcing re-login');
                triggerFullSignOut(
                  'expired',
                  new Error(validationResult.message || 'Your session has expired due to inactivity. Please log in again.')
                );
                return;
              }
              
              if (!validationResult.isValid && validationResult.reason === 'revoked') {
                logger.log(' [SESSION DEBUG] Session invalid - reason: revoked — full sign-out');
                try {
                  sessionStorage.setItem('force_prompt_login', 'true');
                } catch { /* ignore */ }
                triggerFullSignOut('revoked', undefined, { skipIdpRedirect: false });
                return;
              }
              if (!validationResult.isValid && validationResult.reason === 'error') {
                logger.log(' [SESSION DEBUG] Session invalid - reason:', validationResult.reason);
                triggerFullSignOut(
                  'invalid',
                  new Error(validationResult.message || 'Your session is no longer valid. Please log in again.')
                );
                return;
              }
              
              // Session is valid, but we still need to get full session data for permissions
              // Use getSession to get the full session object
              const { sessionsApi } = createApiInstances(access_token);
              const sessionValidation = await sessionsApi.getSession({ 
                sessionId: parsedSession.sessionId 
              });

              // CRITICAL FAILSAFE: Check for "Access denied" errors and track failures
              if (!sessionValidation?.success && isAccessDeniedError(sessionValidation)) {
                const failureCount = incrementAuthFailureCount();
                const errorCount = incrementAuthErrorCount();
                
                logger.log(` [FAILSAFE] Access denied error detected. Failure count: ${failureCount}/${MAX_FAILURES}, Error count: ${errorCount}/${MAX_FAILURES}`);
                
                // If we've failed 3 times, trigger failsafe cleanup
                if (failureCount >= MAX_FAILURES && errorCount >= MAX_FAILURES) {
                  logger.log(' [FAILSAFE] Maximum failures reached - clearing session and redirecting to login');
                  runFailsafeReLogin();
                  return;
                }
                
                // Set error but don't clear session yet (allow retries up to 3)
                setError(new Error('Authentication timeout - please try again'));
                setIsLoading(false);
                setSessionRestorationCompleted(true);
                return;
              }

              // Reset failure counter on successful session validation
              if (sessionValidation?.success) {
                resetAuthFailureCounts();
              }

              if (sessionValidation?.success && sessionValidation.data) {
              logger.log('AuthProviderClient: Session validated successfully, restoring state...');

              // CRITICAL: Check if the validated session has permissions
              const validatedSession = sessionValidation.data;
              const hasValidatedPermissions = !!(
                validatedSession.currentTenantPermissions || 
                validatedSession.effectivePermissions || 
                validatedSession.currentTenant?.permissions
              );

              if (!hasValidatedPermissions) {
                console.error('❌ [SESSION DEBUG] Backend returned session without permissions!');
                logger.log(' [SESSION DEBUG] Validated session structure:', {
                  sessionId: validatedSession.sessionId,
                  hasCurrentTenantPermissions: !!validatedSession.currentTenantPermissions,
                  hasEffectivePermissions: !!validatedSession.effectivePermissions,
                  hasCurrentTenant: !!validatedSession.currentTenant,
                  currentTenantPermissions: validatedSession.currentTenant?.permissions
                });
                
                // Clear the session and force re-authentication
                localStorage.removeItem('session');
                localStorage.removeItem('availableTenants');
                localStorage.removeItem('currentTenant');
                localStorage.removeItem('authToken');
                localStorage.removeItem('preferred_tenant_id');
                
                setSession(null);
                setAvailableTenants([]);
                setCurrentTenant(null);
                setNeedsEmailEntry(true);
                setIsLoading(false);
                setSessionRestorationCompleted(true);
                return;
              }
              
              // CRITICAL: Don't overwrite session if current session already has permissions
              const currentHasPermissions = !!(
                session?.currentTenantPermissions || 
                session?.effectivePermissions || 
                session?.currentTenant?.permissions
              );

              const validatedHasPermissions = !!(
                sessionValidation.data.currentTenantPermissions || 
                sessionValidation.data.effectivePermissions || 
                sessionValidation.data.currentTenant?.permissions
              );

              logger.log(' [SESSION DEBUG] Permission comparison:', {
                currentHasPermissions,
                validatedHasPermissions,
                shouldUpdate: !currentHasPermissions || validatedHasPermissions
              });

              // Only update session if current doesn't have permissions OR validated has permissions
              if (!currentHasPermissions || validatedHasPermissions) {
                logger.log(' [SESSION DEBUG] Updating session with validated data');
                setSession(sessionValidation.data);
                markSessionValidationStartNow();
              } else {
                logger.log(' [SESSION DEBUG] Keeping current session - validated session missing permissions');
                // Update localStorage with current session to prevent future validation
                localStorage.setItem('session', JSON.stringify(session));
              }
              
              // Use tenants from validated session if available, otherwise from localStorage
              const tenantsToUse = (sessionValidation.data.availableTenants && sessionValidation.data.availableTenants.length > 0) 
                ? sessionValidation.data.availableTenants 
                : parsedTenants;
              
              setAvailableTenants(tenantsToUse);
              resolvePreferredTenantId(tenantsToUse || []);
              setCurrentTenant(
                reconcileCurrentTenantWithSession(sessionValidation.data, parsedCurrentTenant)
              );
              
              // Check if this is a valid session using the same logic as recent sessions
              const hasValidSession = sessionValidation.data.sessionId && 
                                    (sessionValidation.data.currentTenant || parsedCurrentTenant);
              
              if (hasValidSession) {
                logger.log(' [SESSION DEBUG] VALID TENANT SESSION validated with backend');
                setNeedsEnrollment(false);
                setNeedsTenantSelection(false);
                setNeedsEmailEntry(false);
                setError(null);
              } else {
                logger.log(' [SESSION DEBUG] INVALID SESSION after backend validation');
                setNeedsEnrollment(true);
                setNeedsTenantSelection(false);
                setNeedsEmailEntry(false);
              }
              
              // Update localStorage with fresh data
              // CRITICAL: Preserve permissions when updating localStorage
              const sessionToStore = currentHasPermissions && !validatedHasPermissions 
              ? session // Keep current session if it has permissions but validated doesn't
              : sessionValidation.data; // Use validated session if it has permissions

              localStorage.setItem('session', JSON.stringify(sessionToStore));
              if (tenantsToUse && tenantsToUse.length > 0) {
                localStorage.setItem('availableTenants', JSON.stringify(tenantsToUse));
              }
              if (sessionValidation.data.currentTenant || parsedCurrentTenant) {
                localStorage.setItem('currentTenant', JSON.stringify(sessionValidation.data.currentTenant || parsedCurrentTenant));
              }
              
              // Try to restore OIDC user (but not during logout or after logout)
              const logoutCompleted = sessionStorage.getItem('logout_completed') === 'true';
              if (!logoutInProgress && !logoutCompleted) {
                await tryRestoreOidcUser();
              } else {
                if (logoutInProgress) {
                  logger.log(' [SESSION DEBUG] Skipping OIDC user restoration during logout');
                }
                if (logoutCompleted) {
                  logger.log(' [SESSION DEBUG] Skipping OIDC user restoration after logout completion');
                }
              }
              
              setIsLoading(false);
              setSessionRestorationCompleted(true);
              return;
            } else {
              logger.log('AuthProviderClient: Session validation failed, clearing stored data');
              
              // CRITICAL FAILSAFE: Check for "Access denied" errors
              if (isAccessDeniedError(sessionValidation)) {
                const failureCount = incrementAuthFailureCount();
                const errorCount = incrementAuthErrorCount();
                
                logger.log(` [FAILSAFE] Access denied error in else block. Failure count: ${failureCount}/${MAX_FAILURES}, Error count: ${errorCount}/${MAX_FAILURES}`);
                
                // If we've failed 3 times, trigger failsafe cleanup
                if (failureCount >= MAX_FAILURES && errorCount >= MAX_FAILURES) {
                  logger.log(' [FAILSAFE] Maximum failures reached - clearing session and redirecting to login');
                  runFailsafeReLogin();
                  return;
                }
                
                // Set error but allow retries up to 3
                setError(new Error('Authentication timeout - please try again'));
                setIsLoading(false);
                setSessionRestorationCompleted(true);
                return;
              }
              
              clearAuthForReLogin(new Error('Session validation failed. Please log in again.'));
              return;
            }

          } catch (apiError: any) {
            logger.log(' [SESSION DEBUG] Session validation API call failed:', apiError);
            
            const status = apiError?.status ?? apiError?.response?.status;
            const errorCode = String(apiError?.errorCode || apiError?.response?.data?.errorCode || '').toUpperCase();

            const requestUrl = String(apiError?.url || apiError?.response?.config?.url || '').toLowerCase();
            const isSessionEndpoint = requestUrl.includes('/api/v1/sessions/');

            if (
              (status === 401 && errorCode === 'SESSION_INVALID') ||
              (status === 401 && !errorCode && isSessionEndpoint)
            ) {
              logger.log(' [SESSION DEBUG] Session invalid 401 - clearing data and forcing re-login', {
                errorCode: errorCode || 'missing',
                requestUrl,
              });
              triggerFullSignOut('expired', new Error('Your session has expired. Please log in again.'));
              return;
            }
            
            if (apiError.status === 403 || apiError.response?.status === 403) {
              logger.log(' [SESSION DEBUG] 403 response - session cleaned up or revoked, redirecting to login');
              triggerFullSignOut('cleaned_up', new Error('Your session is no longer valid. Please log in again.'));
              return;
            }
            
            // For other API errors, log and continue with normal flow
            console.warn(' [SESSION DEBUG] Session validation API error (non-403):', apiError);
            // Don't clear data for other errors - let the outer catch handle it
          }

          } catch (sessionError) {
            console.error('❌ [SESSION DEBUG] Session restoration failed:', sessionError);
            
            // CRITICAL FIX: Handle network errors gracefully - don't set blocking errors for transient network issues
            const isNetworkError = sessionError instanceof Error && (
              sessionError.message.includes('NetworkError') ||
              sessionError.message.includes('fetch resource') ||
              sessionError.message.includes('Failed to fetch') ||
              sessionError.message.includes('network') ||
              sessionError.message.includes('CORS')
            );
            
            if (isNetworkError) {
              // For network errors, log but don't set a blocking error - let the user retry naturally
              console.warn(' [SESSION DEBUG] Network error during session restoration - will retry on next action');
              // Clear potentially corrupted session data but don't block the UI
              localStorage.removeItem('session');
              localStorage.removeItem('availableTenants');
              localStorage.removeItem('currentTenant');
              localStorage.removeItem('authToken');
              localStorage.removeItem('preferred_tenant_id');
              // Continue to OIDC user check below instead of stopping
            } else {
              // For non-network errors, clear corrupted session data
              localStorage.removeItem('session');
              localStorage.removeItem('availableTenants');
              localStorage.removeItem('currentTenant');
              localStorage.removeItem('authToken');
              localStorage.removeItem('preferred_tenant_id');
              // Continue to OIDC user check below instead of stopping
            }
          }
        } else {
          logger.log(' [SESSION DEBUG] No stored session data found', {
            hasAuthToken: !!authTokenData,
            hasSession: !!storedSession,
            hasValidAuthToken,
            hasValidSession,
            authTokenEmpty: !authTokenData || authTokenData.trim().length === 0,
            sessionEmpty: !storedSession || storedSession.trim().length === 0
          });
        }

        // Step 2: Check for OIDC user if no valid session (but not during logout or after logout)
        // CRITICAL: Check for logout completion flag to prevent auto-login after logout
        const logoutCompleted = sessionStorage.getItem('logout_completed') === 'true';
        if (logoutCompleted) {
          logger.log('AuthProviderClient: Logout was completed, skipping OIDC user restoration to prevent auto-login');
          // Clear the flag after checking (one-time use)
          sessionStorage.removeItem('logout_completed');
          sessionStorage.removeItem('logout_timestamp');
        }
        
        if (!logoutInProgress && !logoutCompleted) {
          logger.log('AuthProviderClient: No valid stored session, checking for OIDC user...');

          const restored = await tryRestoreOidcUser();
          if (restored) {
            setSessionRestorationCompleted(true);
            // handleUserLoaded / ensureSessionForUser will create session
            return;
          }
        } else {
          if (logoutInProgress) {
            logger.log(' [SESSION DEBUG] Skipping OIDC user check during logout');
          }
          if (logoutCompleted) {
            logger.log(' [SESSION DEBUG] Skipping OIDC user check after logout completion');
          }
        }

        // Step 3: No authentication found
        logger.log('AuthProviderClient: No existing authentication found, showing login');
        setNeedsEmailEntry(true);
        setIsLoading(false);
        setSessionRestorationCompleted(true);
        
      } catch (err) {
        console.error('❌ [SESSION DEBUG] Error in session restoration:', err);
        
        // CRITICAL FIX: Handle network errors gracefully - don't set blocking errors for transient network issues
        const isNetworkError = err instanceof Error && (
          err.message.includes('NetworkError') ||
          err.message.includes('fetch resource') ||
          err.message.includes('Failed to fetch') ||
          err.message.includes('network') ||
          err.message.includes('CORS')
        );
        
        if (isNetworkError) {
          // For network errors, log but don't set a blocking error - let the user retry naturally
          console.warn(' [SESSION DEBUG] Network error during session restoration - user can retry login');
          setNeedsEmailEntry(true);
          setIsLoading(false);
          setSessionRestorationCompleted(true);
          // Don't set error - let user proceed to login page naturally
        } else {
          // For non-network errors, set error state
          setNeedsEmailEntry(true);
          setIsLoading(false);
          setSessionRestorationCompleted(true);
          // Only set error for non-network issues
          setError(err instanceof Error ? err : new Error('Session restoration failed. Please try logging in again.'));
        }
      }
    };
    
    // AGGRESSIVE CHECK: If logout is in progress, don't restore anything
    if (logoutInProgress) {
      logger.log(' [MAIN EFFECT] Logout in progress, skipping all session restoration');
      return;
    }
    
    // Check for logout flag in sessionStorage
    const logoutFlag = sessionStorage.getItem('logout_in_progress');
    if (logoutFlag === 'true') {
      logger.log(' [MAIN EFFECT] Logout flag detected, skipping all session restoration');
      sessionStorage.removeItem('logout_in_progress');
      setNeedsEmailEntry(true);
      setIsLoading(false);
      setSessionRestorationCompleted(true);
      return;
    }
    
    restoreSession();
  }, [pathname, sessionRestorationCompleted, logoutInProgress, runFailsafeReLogin, clearAuthForReLogin, tryRestoreOidcUser]);

  // Single linear flow: only place that triggers session creation when user is present and session is missing (callback sets user, this effect runs).
  useEffect(() => {
    if (logoutInProgress) return;
    if (isE2eSkipSessionMode()) return;
    if (!user || session || userProcessingCompleted) return;

    logger.log(' [USER PROCESSING] User without session, calling ensureSessionForUser:', user.profile.email);
    ensureSessionForUser(user);
  }, [user, session, ensureSessionForUser, userProcessingCompleted, logoutInProgress]);

  // Navigate away from callback once user is processed
  useEffect(() => {
    if (pathname === '/auth/callback' && user && !isLoading && !error) {
      router.push('/');
    }
  }, [pathname, user, isLoading, error, router]);

  // Domain-aware login handlers
  const login = useCallback(async (method: 'sso' | 'local' | 'auto', email?: string, domainConfig?: any) => {
    try {
      logger.log('AuthProviderClient: Initiating domain-aware login with method:', method, 'email:', email, 'domainConfig:', domainConfig);
      clearStaleClientSessionForLogin();
      setIsLoading(true);

      if (email && domainConfig) {
        // Use the domain configuration provided by domain-aware login
        logger.log('AuthProviderClient: Using provided domain configuration');
        
        // Store domain configuration for session creation
        setPendingDomainConfig(domainConfig);
        localStorage.setItem('pendingDomainConfig', JSON.stringify(domainConfig));
        
        // Respect domain discovery: only redirect to SSO when domain has SSO and provides oidcConfig (never use env OIDC for local domains)
        const hasSSO =
          domainConfig.providerType &&
          domainConfig.providerType !== 'local' &&
          !!domainConfig.oidcConfig;
        logger.log(' [AUTH DEBUG] SSO availability from domain config:', { hasSSO, providerType: domainConfig.providerType, hasOidcConfig: !!domainConfig.oidcConfig });
        
        if ((method === 'sso' || method === 'auto') && hasSSO) {
          logger.log(' [AUTH DEBUG] Redirecting to SSO with domain config:', domainConfig);
          await redirectToSpecificSSO(domainConfig, email);
        } else {
          logger.log(' [AUTH DEBUG] Redirecting to local auth');
          await redirectToLocalAuth(email);
        }
      } else if (email) {
        // No domain config provided, use local auth as fallback
        logger.log('AuthProviderClient: No domain config provided, using local auth as fallback');
        await redirectToLocalAuth(email);
      } else {
        // Fallback to local auth without domain discovery
        logger.log('AuthProviderClient: Using local auth (no domain discovery)');
        await redirectToLocalAuth(email);
      }
    } catch (err) {
      // Handle domain-aware login errors gracefully
      setError(err instanceof Error ? err : new Error('Failed to initiate login'));
      setIsLoading(false);
    }
  }, [clearStaleClientSessionForLogin]);

  // CU-18: Generate OAuth state parameter for CSRF protection
  const generateState = useCallback((): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }, []);

  // CU-18: Generate OAuth nonce parameter for replay protection
  const generateNonce = useCallback((): string => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }, []);


  // Helper function to redirect to specific SSO provider using oidc-client-ts
  const redirectToSpecificSSO = useCallback(async (result: any, email: string) => {
    logger.log(' [AUTH DEBUG] redirectToSpecificSSO called with:', { result, email });
    
    // Use oidc-client-ts signinRedirect() for proper OAuth flow with PKCE
    if (result.oidcConfig) {
      // Create UserManager with the OIDC config from backend
      const userManager = createUserManagerForOidcConfig(result.oidcConfig);
      if (!userManager) {
        throw new Error('Failed to create UserManager with OIDC configuration');
      }

      // Store OIDC config so we can recreate UserManager in callback
      // The library handles state/nonce/PKCE automatically
      sessionStorage.setItem('oidc_config_for_callback', JSON.stringify({
        issuer: result.oidcConfig.issuer,
        clientId: result.oidcConfig.clientId,
        tokenEndpointPath: result.oidcConfig.tokenEndpointPath
      }));
      saveOidcConfigForRestore(result.oidcConfig);

      saveLoginDomainConfigForCallback(result, email);

      // Build extra query parameters for identity provider hints
      const extraQueryParams: Record<string, string> = {
        login_hint: email
      };

      // After idle timeout / forced re-login, require interactive credentials (H01)
      try {
        if (sessionStorage.getItem('force_prompt_login') === 'true') {
          extraQueryParams.prompt = 'login';
          sessionStorage.removeItem('force_prompt_login');
        }
      } catch {
        /* ignore */
      }

      // Add identity provider hint (provider-specific parameter name)
      if (result.oidcConfig.idpAlias && result.oidcConfig.idpHintParamName) {
        extraQueryParams[result.oidcConfig.idpHintParamName] = result.oidcConfig.idpAlias;
      } else if (result.keycloakAlias) {
        // Fallback to Keycloak-specific parameter
        extraQueryParams['kc_idp_hint'] = result.keycloakAlias;
      }

      logger.log(' [AUTH DEBUG] Using oidc-client-ts signinRedirect with:', {
        issuer: result.oidcConfig.issuer,
        clientId: result.oidcConfig.clientId,
        redirect_uri: `${window.location.origin}/auth/callback`,
        extraQueryParams
      });

      // Debug: Log all localStorage keys before redirect to see what oidc-client-ts stores
      const allLocalStorageKeys = Object.keys(localStorage).filter(key => key.startsWith('oidc.'));
      logger.log(' [AUTH DEBUG] localStorage keys before redirect:', allLocalStorageKeys);

      // Use the library's signinRedirect - it handles PKCE, state, nonce automatically
      // The library stores state in localStorage with a key based on settings
      await userManager.signinRedirect({
        extraQueryParams
      });

      // Debug: Log localStorage keys after signinRedirect to see what was stored
      // Note: This might not execute if redirect happens immediately, but it's worth trying
      setTimeout(() => {
        const keysAfter = Object.keys(localStorage).filter(key => key.startsWith('oidc.'));
        logger.log(' [AUTH DEBUG] localStorage keys after signinRedirect:', keysAfter);
      }, 100);

      return; // Exit early - redirect is happening
    }

    // Fallback: Use Keycloak OIDC flow with identity provider hint
    const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY;
    const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID;
    
    logger.log(' [AUTH DEBUG] OIDC config:', { authority, clientId });
    
    if (!authority || !clientId) {
      throw new Error('OIDC configuration missing');
    }

    // Get SSO provider alias from the domain discovery result
    let providerAlias = result.oidcConfig?.idpAlias || result.keycloakAlias || result.providerType;
    
    // Handle legacy alias format - if it ends with "-realm", try the new format
    if (providerAlias && providerAlias.endsWith('-realm') && result.providerType && result.domain) {
      const newAlias = `${result.providerType}-${result.domain.replace(/\./g, '-')}`;
      logger.log(' [AUTH DEBUG] Converting legacy alias format:', {
        oldAlias: providerAlias,
        newAlias: newAlias,
        providerType: result.providerType,
        domain: result.domain
      });
      providerAlias = newAlias;
    }
    
    logger.log(' [AUTH DEBUG] Provider alias extraction:', {
      keycloakAlias: result.keycloakAlias,
      providerType: result.providerType,
      finalProviderAlias: providerAlias
    });

    if (!providerAlias) {
      const errorMsg = 'No SSO provider alias found. The identity provider may not be properly configured. Please contact your administrator.';
      logger.error('AuthProviderClient: No SSO provider alias found', {
        result,
        idpAlias: result.oidcConfig?.idpAlias,
        keycloakAlias: result.keycloakAlias,
        providerType: result.providerType,
        domain: result.domain
      });
      console.warn('AuthProviderClient: No SSO provider alias found, falling back to local auth');
      throw new Error(errorMsg);
    }

    logger.log('AuthProviderClient: Redirecting to SSO provider:', {
      providerAlias,
      result,
      idpAlias: result.oidcConfig?.idpAlias,
      keycloakAlias: result.keycloakAlias,
      providerType: result.providerType,
      domain: result.domain,
      email
    });

    // Create UserManager for this specific auth flow
    const userManager = createUserManager(authority);
    
    // Try multiple approaches for identity provider redirection
    const extraParams: Record<string, string> = {
      login_hint: email
    };

    // Try different parameter names for identity provider hint
    if (providerAlias) {
      extraParams.kc_idp_hint = providerAlias;
      extraParams.idp_hint = providerAlias;
      extraParams.kc_idp = providerAlias;
    }

    logger.log('AuthProviderClient: Using extra params for SSO redirect:', extraParams);
    
    // Try the oidc-client-ts approach first
    try {
      await userManager.signinRedirect({
        extraQueryParams: extraParams
      });
    } catch (error) {
      console.warn('AuthProviderClient: oidc-client-ts approach failed, trying direct URL construction:', error);
      
      // Fallback: Construct Keycloak URL directly
      const keycloakUrl = new URL('/protocol/openid-connect/auth', authority);
      keycloakUrl.searchParams.set('client_id', clientId);
      keycloakUrl.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`);
      keycloakUrl.searchParams.set('response_type', 'code');
      keycloakUrl.searchParams.set('scope', 'openid profile email');
      keycloakUrl.searchParams.set('kc_idp_hint', providerAlias);
      keycloakUrl.searchParams.set('login_hint', email);
      
      logger.log('AuthProviderClient: Redirecting to direct Keycloak URL:', keycloakUrl.toString());
      window.location.href = keycloakUrl.toString();
    }
  }, [createUserManagerForOidcConfig]);

  // Helper function to redirect to local Keycloak auth
  const redirectToLocalAuth = useCallback(async (email?: string, forAccountCreation: boolean = false) => {
    const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY;
    const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID;
    
    if (!authority || !clientId) {
      throw new Error('OIDC configuration missing');
    }

    logger.log('AuthProviderClient: Redirecting to local Keycloak auth', forAccountCreation ? 'for account creation' : '');

    // Create UserManager for this specific auth flow
    const userManager = createUserManager(authority);
    
    // Use the library's signinRedirect method with login_hint and optional registration hint
    const extraParams: Record<string, string> = {
      login_hint: email || ''
    };
    
    // If this is for account creation, add a hint to Keycloak
    if (forAccountCreation) {
      extraParams.kc_action = 'register';
    }
    
    await userManager.signinRedirect({
      extraQueryParams: extraParams
    });
  }, []);

  // Function to refresh session after enrollment completion
  const refreshSessionAfterEnrollment = useCallback(async () => {
    logger.log('AuthProviderClient: refreshSessionAfterEnrollment called');
    
    if (!user) {
      console.error('AuthProviderClient: Cannot refresh session - no user available');
      throw new Error('Cannot refresh session: User not authenticated');
    }
    
    try {
      // Force a fresh session creation
      setDataRefreshTrigger(prev => prev + 1);
      logger.log('AuthProviderClient: Session refresh trigger updated');
    } catch (error) {
      console.error('AuthProviderClient: Error in refreshSessionAfterEnrollment:', error);
      throw error;
    }
  }, [user]);

  // Administrative remint: Force a new session to be minted with the current tenant context
  const remintSession = useCallback(async () => {
    if (!user || !currentTenant?.tenantId) {
      throw new Error('Cannot remint session: missing user or current tenant');
    }
    try {
      logger.log('AuthProviderClient: Reminting session (mint new session) for tenant:', currentTenant.tenantId);
      const jwtToken = user.access_token;
      if (!jwtToken) throw new Error('No JWT token available for remint');

      const { sessionsApi } = createApiInstances(jwtToken);

      const email = (user.profile.email as string) || '';
      const userProfile: UserProfileDto = {
        userId: user.profile.sub || user.profile.preferred_username || email,
        email,
        name: user.profile.name || (user.profile.given_name && user.profile.family_name ? `${user.profile.given_name} ${user.profile.family_name}` : user.profile.given_name || user.profile.family_name || email),
        givenName: user.profile.given_name,
        familyName: user.profile.family_name,
        picture: user.profile.picture,
        preferredUsername: user.profile.preferred_username,
      };

      const sessionRequest: CreateSessionRequest = {
        userProfile,
        currentTenantId: currentTenant.tenantId,
        metadata: {
          user_agent: navigator.userAgent,
          login_method: 'oidc',
        },
      };

      const sessionResponse: SessionResponse = await sessionsApi.createSession({
        createSessionRequest: sessionRequest
      });
      if (!sessionResponse.success) {
        throw new Error(sessionResponse.error || 'Failed to remint session');
      }

      const newSession = sessionResponse.data!;
      // Reset failure counters on successful session remint
      resetAuthFailureCounts();
      setSession(newSession);
      setAvailableTenants(newSession.availableTenants || []);
      if (newSession.currentTenant) {
        setCurrentTenant(newSession.currentTenant);
        localStorage.setItem('preferred_tenant_id', newSession.currentTenant.tenantId!);
      }
      persistSessionToStorage({
        session: newSession,
        availableTenants: newSession.availableTenants,
        currentTenant: newSession.currentTenant,
      });
      setLastTenantSwitchAt(Date.now());
      setDataRefreshTrigger(prev => prev + 1);

      // Post-remint, proactively hydrate available tenants in case backend adds relationships slightly after session mint
      try {
        const authTokenData = localStorage.getItem('authToken');
        if (authTokenData && newSession?.sessionId) {
          const { access_token } = JSON.parse(authTokenData);
          const { sessionsApi } = createApiInstances(access_token);
          const baselineCount = Array.isArray(newSession?.availableTenants) ? newSession.availableTenants.length : 0;
          // Use throttled fetcher to avoid duplicate concurrent calls and 429s
          const list = await fetchAvailableTenantsThrottled();
          if (Array.isArray(list) && list.length > baselineCount) {
            setAvailableTenants(list);
          }
        }
      } catch (e) {
        console.warn('AuthProviderClient: post-remint hydration failed', e);
      }
    } catch (e) {
      console.error('AuthProviderClient: remintSession failed', e);
      throw e;
    }
  }, [user, currentTenant?.tenantId, createApiInstances]);

  useEffect(() => {
    remintSessionRef.current = remintSession;
  }, [remintSession]);
  useEffect(() => {
    runFailsafeReLoginRef.current = runFailsafeReLogin;
  }, [runFailsafeReLogin]);

  // Poll the session service until the target tenant appears in available-tenants (with short backoff)
  const ensureTenantAccess = useCallback(async (tenantId: string): Promise<boolean> => {
    logger.log(' [ENSURE-TENANT-ACCESS] Checking access for tenant:', tenantId);
    logger.log(' [ENSURE-TENANT-ACCESS] Session ID:', session?.sessionId);
    
    if (!session?.sessionId) {
      logger.log(' [ENSURE-TENANT-ACCESS] No session ID, returning false');
      return false;
    }
    
    try {
      logger.log(' [ENSURE-TENANT-ACCESS] Fetching available tenants...');
      const list = await fetchAvailableTenantsThrottled();
      logger.log(' [ENSURE-TENANT-ACCESS] Fetched tenants:', list);
      
      if (Array.isArray(list)) {
        setAvailableTenants(prev => {
          const map = new Map<string, TenantAccessDto>();
          for (const t of list) if (t?.tenantId) map.set(t.tenantId, t);
          for (const t of prev || []) if (t?.tenantId && !map.has(t.tenantId)) map.set(t.tenantId, t);
          return Array.from(map.values());
        });
        const hasAccess = list.some(t => t?.tenantId === tenantId);
        logger.log(' [ENSURE-TENANT-ACCESS] Has access result:', hasAccess);
        return hasAccess;
      }
      logger.log(' [ENSURE-TENANT-ACCESS] No valid tenant list, returning false');
      return false;
    } catch (error) {
      console.error(' [ENSURE-TENANT-ACCESS] Error fetching tenants:', error);
      return false;
    }
  }, [session?.sessionId, createApiInstances]);

  const logout = useCallback(async () => {
    try {
      logger.log('AuthProviderClient: Starting logout...');
      beginSignOut();
      clearTimezoneCache();
      await forceFullSignOut({
        reason: 'invalid',
        userManager,
        sessionId: session?.sessionId,
        authority: (process.env.NEXT_PUBLIC_OIDC_AUTHORITY || userManager?.settings?.authority || '').toString(),
        clientId: (process.env.NEXT_PUBLIC_OIDC_CLIENT_ID || userManager?.settings?.client_id || '').toString(),
        idTokenHint: (user as User & { id_token?: string })?.id_token,
        postLogoutRedirectUri: `${window.location.origin}/auth/logout`,
        setSessionMessage: false,
        skipIdpRedirect: false,
        onBeforeStorageClear: () => {
          clearAuthForReLogin(undefined, { preventRestoration: true });
          setUser(null);
          setError(null);
        },
        onAfterStorageClear: () => setUserManager(null),
      });
    } catch (err) {
      console.error('AuthProviderClient: Logout error:', err);
      clearAuthForReLogin(err instanceof Error ? err : new Error('Logout failed'), { preventRestoration: true });
      setUser(null);
      setUserManager(null);
      window.location.href = '/';
    }
  }, [userManager, session, user, clearAuthForReLogin, beginSignOut]);

  // Debounced version of createOrUpdateSession for user-triggered actions (like selectTenant)
  const debouncedCreateOrUpdateSession = useCallback((user: User, tenants: TenantAccessDto[], selectedTenantId: string, delay: number = 300) => {
    // Clear any existing timer
    if (sessionCreationTimerRef.current) {
      clearTimeout(sessionCreationTimerRef.current);
    }

    // Store the latest request
    pendingSessionRequestRef.current = { user, tenants, selectedTenantId };

    // Set a new timer
    sessionCreationTimerRef.current = setTimeout(() => {
      const request = pendingSessionRequestRef.current;
      if (request) {
        createOrUpdateSession(request.user, request.tenants, request.selectedTenantId).catch(error => {
          console.error('AuthProviderClient: Error in debounced session creation:', error);
        });
        pendingSessionRequestRef.current = null;
      }
      sessionCreationTimerRef.current = null;
    }, delay);
  }, [createOrUpdateSession]);

  const selectTenant = useCallback(async (tenantId: string) => {
    logger.log('AuthProviderClient: selectTenant called with tenantId:', tenantId);
    
    if (!user || !availableTenants.length) {
      console.error('AuthProviderClient: Cannot select tenant - missing user or availableTenants');
      throw new Error('Cannot select tenant: User not authenticated or no tenants available');
    }
    
    // Use debounced session creation for user-triggered tenant selection
    debouncedCreateOrUpdateSession(user, availableTenants, tenantId);
    logger.log('AuthProviderClient: Tenant selection queued (debounced)');
  }, [user, availableTenants, debouncedCreateOrUpdateSession]);

  // Prevent overlapping tenant switches to avoid loops
  const switchingTenantRef = useRef<string | null>(null);
  const switchingTenantPromiseRef = useRef<Map<string, Promise<MultiTenantSessionDto>>>(new Map());

  const switchTenant = useCallback(async (tenantId: string): Promise<MultiTenantSessionDto> => {
    logger.log('AuthProviderClient: switchTenant called with tenantId:', tenantId);

    // Aegis session + Bearer is enough; OIDC user may be missing after hard refresh.
    if (!session?.sessionId) {
      console.error('AuthProviderClient: Cannot switch tenant - missing session');
      throw new Error('Cannot switch tenant: no active session');
    }
    if (!getAuthToken() && !user?.access_token) {
      console.error('AuthProviderClient: Cannot switch tenant - missing Aegis access token');
      throw new Error('Cannot switch tenant: missing session access token');
    }

    const inFlight = switchingTenantPromiseRef.current.get(tenantId);
    if (inFlight) {
      logger.log('AuthProviderClient: Tenant switch already in progress for', tenantId, '— awaiting existing promise');
      return inFlight;
    }

    if (session.currentTenant?.tenantId === tenantId) {
      // If we already think we're on this tenant but only have one visible tenant, force a refresh to hydrate availableTenants
      const needsRefresh = (availableTenants?.length || 0) <= 1;
      if (!needsRefresh) {
        logger.log('AuthProviderClient: Already on target tenant', tenantId, '— no-op');
        return Promise.resolve(session);
      }
      logger.log('AuthProviderClient: Forcing session refresh on same-tenant switch to hydrate availableTenants');
    }

    const SWITCH_TIMEOUT_MS = 15000;
    const switchPromise = (async (): Promise<MultiTenantSessionDto> => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      try {
        switchingTenantRef.current = tenantId;
        timeoutId = setTimeout(() => {
          if (switchingTenantRef.current === tenantId) {
            logger.log('AuthProviderClient: Tenant switch timeout — clearing in-progress ref');
            switchingTenantRef.current = null;
            switchingTenantPromiseRef.current.delete(tenantId);
          }
        }, SWITCH_TIMEOUT_MS);
        logger.log('AuthProviderClient: Calling createOrUpdateSession for tenant switch:', tenantId);
        const updated = await createOrUpdateSession(user, availableTenants, tenantId);
        logger.log('AuthProviderClient: Tenant switch completed successfully');
        // After a successful switch (or same-tenant refresh), double-check available tenants and hydrate if still singleton
        try {
          const count = (updated?.availableTenants?.length ?? availableTenants?.length ?? 0);
          if (count <= 1 && updated?.sessionId) {
            allowAvailableTenantsFetchRef.current = true;
            const list = await fetchAvailableTenantsThrottled();
            if (Array.isArray(list) && list.length > count) {
              logger.log('AuthProviderClient: Post-switch hydration of availableTenants from API:', list);
              setAvailableTenants(list);
            }
            allowAvailableTenantsFetchRef.current = false;
          }
        } catch (e) {
          console.warn('AuthProviderClient: Post-switch hydrate failed:', e);
        }
        setLastTenantSwitchAt(Date.now());
        setDataRefreshTrigger(prev => {
          const newValue = prev + 1;
          logger.log(' [AUTH] Triggering data refresh after tenant switch, new trigger value:', newValue);
          return newValue;
        });
        return updated;
      } catch (error) {
        console.error('AuthProviderClient: Error in switchTenant:', error);
        throw error;
      } finally {
        if (timeoutId != null) clearTimeout(timeoutId);
        switchingTenantRef.current = null;
        switchingTenantPromiseRef.current.delete(tenantId);
      }
    })();

    switchingTenantPromiseRef.current.set(tenantId, switchPromise);
    return switchPromise;
  }, [user, session, availableTenants, createOrUpdateSession, getAuthToken]);

  // Sync in-memory session id from storage on mount — never hydrate on logout/callback routes
  // (LogoutPage clears localStorage in a child effect; hydrating here would poison activeSessionId).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (path === '/auth/logout' || path === '/auth/callback' || path === '/auth/silent-callback') {
      setActiveSessionId(null);
      return;
    }
    setActiveSessionId(readSessionIdFromLocalStorage());
  }, []);

  // Clear poisoned React session state whenever the logout route is shown (localStorage alone is not enough).
  useEffect(() => {
    if (pathname === '/auth/logout') {
      clearStaleClientSessionForLogin();
    }
  }, [pathname, clearStaleClientSessionForLogin]);

  // Add this at the top of AuthProviderClient to track state changes
  useEffect(() => {
    logger.log(' Auth State Debug:', {
      hasUser: !!user,
      hasSession: !!session,
      hasCurrentTenant: !!currentTenant,
      sessionId: session?.sessionId,
      sessionDetails: session ? {
        sessionId: session.sessionId,
        currentTenantId: session.currentTenant?.tenantId,
        availableTenantsCount: session.availableTenants?.length
      } : null,
      isLoading,
      needsTenantSelection,
      needsEmailEntry,
      pathname,
      error: error?.message
    });
  }, [user, session, currentTenant, isLoading, needsTenantSelection, needsEmailEntry, pathname, error]);

  // Prevent infinite loading: after 10s force reset so user can try again.
  // Only run when we have user or session (e.g. creating session or restoring). Do not run on bare
  // login screen where isLoading can be true from restoration attempt with no auth state yet.
  useEffect(() => {
    if (isSecurityDashboardPdfPrintBrowserContext()) return
    if (isLoading && (user || session)) {
      const timeout = setTimeout(() => {
        console.warn('AuthProviderClient: Authentication timeout - forcing full sign-out');
        setIsLoading(false);
        triggerFullSignOut('expired', new Error('Authentication timeout - please try again'));
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [isLoading, user, session, triggerFullSignOut]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (sessionCreationTimerRef.current) {
        clearTimeout(sessionCreationTimerRef.current);
        sessionCreationTimerRef.current = null;
      }
      pendingSessionRequestRef.current = null;
    };
  }, []);

  // Persist session, tenants, and current tenant to localStorage
  useEffect(() => {
    if (authStorageFrozenRef.current) {
      return;
    }
    if (session) {
      if (!session.sessionId && isE2eSkipSessionMode()) {
        // Never overwrite Playwright-seeded session with a broken in-memory session.
        return;
      }
      if (session.sessionId) {
        setActiveSessionId(session.sessionId);
      }
      localStorage.setItem('session', JSON.stringify(session));
    }
    if (availableTenants) {
      localStorage.setItem('availableTenants', JSON.stringify(availableTenants));
    }
    if (currentTenant) {
      localStorage.setItem('currentTenant', JSON.stringify(currentTenant));
    }
  }, [session, availableTenants, currentTenant]);

  // SECURITY (pentest finding C01 core fix): proactively refresh the short-lived Aegis-signed
  // session access token (~10 min TTL - see AegisSessionTokenService) BEFORE it expires,
  // independent of the OIDC token's own lifetime/silent-renew schedule. Without this, every
  // session would hard-fail with 401s roughly 10 minutes after login/tenant-switch regardless of
  // how fresh the user's OIDC token still is.
  useEffect(() => {
    const sessionId = session?.sessionId;
    const expiresAt = session?.accessTokenExpiresAt;
    if (!sessionId || !expiresAt) {
      return;
    }

    const expiresAtMs = new Date(expiresAt as unknown as string).getTime();
    if (!Number.isFinite(expiresAtMs)) {
      return;
    }

    // Refresh 90s before expiry, but never schedule a negative/immediate-loop delay.
    const REFRESH_MARGIN_MS = 90 * 1000;
    const delay = Math.max(5000, expiresAtMs - Date.now() - REFRESH_MARGIN_MS);

    const timer = setTimeout(async () => {
      try {
        const result = await refreshSessionAccessToken(sessionId);
        if (result.success && result.session) {
          logger.debug('AuthProviderClient: Aegis session access token refreshed proactively');
          setSession(prev => (prev && prev.sessionId === sessionId ? { ...prev, ...result.session } : prev));
        } else {
          logger.warn('AuthProviderClient: Proactive access token refresh failed; next request may 401 and trigger normal session-invalid handling');
        }
      } catch (e) {
        console.warn('AuthProviderClient: Proactive access token refresh threw:', e);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [session?.sessionId, session?.accessTokenExpiresAt]);

  // Handle tenant decommissioned event - manually remove from availableTenants and session.selectableTenantIds
  // This avoids calling remintSession/refreshSession which recalculates and may lose decommissioned tenants
  useEffect(() => {
    const handleTenantDecommissioned = (event: CustomEvent<{ tenantId: string }>) => {
      const decommissionedTenantId = event.detail?.tenantId;
      if (!decommissionedTenantId) return;
      
      logger.log(' [AUTH] Tenant decommissioned event received:', decommissionedTenantId);
      
      // Remove the decommissioned tenant from availableTenants
      setAvailableTenants(prev => {
        const filtered = (prev || []).filter(t => t.tenantId !== decommissionedTenantId);
        logger.log(' [AUTH] Removed decommissioned tenant from availableTenants:', {
          removedTenantId: decommissionedTenantId,
          previousCount: prev?.length || 0,
          newCount: filtered.length
        });
        return filtered;
      });
      
      // Also remove from session.selectableTenantIds and selectedTenantIds if they exist
      // This is critical for the Select Tenants dialog to update immediately
      setSession(prev => {
        if (!prev) return prev;
        const updatedSelectableTenantIds = (prev.selectableTenantIds || []).filter(
          id => id !== decommissionedTenantId
        );
        let updatedSelectedTenantIds = (prev.selectedTenantIds || []).filter(
          id => id !== decommissionedTenantId
        );
        
        // Ensure at least one tenant remains selected (backend requirement)
        // Prefer current tenant if available, otherwise use first selectable tenant
        if (updatedSelectedTenantIds.length === 0 && updatedSelectableTenantIds.length > 0) {
          const fallbackTenantId = currentTenant?.tenantId && updatedSelectableTenantIds.includes(currentTenant.tenantId)
            ? currentTenant.tenantId
            : updatedSelectableTenantIds[0];
          if (fallbackTenantId) {
            updatedSelectedTenantIds = [fallbackTenantId];
            logger.log(' [AUTH] selectedTenantIds became empty after decommission, adding fallback tenant:', {
              fallbackTenantId,
              wasCurrentTenant: fallbackTenantId === currentTenant?.tenantId
            });
          }
        }
        
        // Check if either list changed
        const selectableChanged = updatedSelectableTenantIds.length !== (prev.selectableTenantIds?.length || 0);
        const selectedChanged = updatedSelectedTenantIds.length !== (prev.selectedTenantIds?.length || 0) ||
          (updatedSelectedTenantIds.length > 0 && !updatedSelectedTenantIds.every(id => prev.selectedTenantIds?.includes(id)));
        
        if (selectableChanged || selectedChanged) {
          if (selectableChanged) {
            logger.log(' [AUTH] Removed decommissioned tenant from session.selectableTenantIds:', {
              removedTenantId: decommissionedTenantId,
              previousCount: prev.selectableTenantIds?.length || 0,
              newCount: updatedSelectableTenantIds.length
            });
          }
          if (selectedChanged) {
            logger.log(' [AUTH] Removed decommissioned tenant from session.selectedTenantIds:', {
              removedTenantId: decommissionedTenantId,
              previousCount: prev.selectedTenantIds?.length || 0,
              newCount: updatedSelectedTenantIds.length
            });
          }
          return {
            ...prev,
            selectableTenantIds: updatedSelectableTenantIds,
            selectedTenantIds: updatedSelectedTenantIds
          };
        }
        return prev;
      });
      
      // If the decommissioned tenant is the current tenant, we need to switch to another tenant
      if (currentTenant?.tenantId === decommissionedTenantId) {
        logger.log(' [AUTH] Current tenant was decommissioned, will need to switch');
        // The app-layout component will handle auto-switching via isCurrentTenantDecommissioned check
      }
      
      // Trigger data refresh
      setDataRefreshTrigger(prev => prev + 1);
    };
    
    try {
      window.addEventListener('tenantDecommissioned', handleTenantDecommissioned as EventListener);
    } catch {}
    
    return () => {
      try {
        window.removeEventListener('tenantDecommissioned', handleTenantDecommissioned as EventListener);
      } catch {}
    };
  }, [currentTenant?.tenantId]);

  // Bindings/config awareness: refresh session on external change signals
  useEffect(() => {
    const handler = async () => {
      try {
        if (isE2eSkipSessionMode()) {
          return;
        }
        // If a tenant switch is actively in progress, skip external refresh to avoid state flip-flop
        if (switchingTenantRef.current) {
          logger.log('AuthProviderClient: Skipping external session refresh during active tenant switch');
          return;
        }
        const authoritativeSessionId = resolveSessionIdForRequests() ?? session?.sessionId;
        if (!authoritativeSessionId) return;
        const authTokenData = localStorage.getItem('authToken');
        if (!authTokenData) return;
        const { access_token } = JSON.parse(authTokenData);
        const { sessionsApi } = createApiInstances(access_token);
        const resp = await sessionsApi.getSession({ sessionId: authoritativeSessionId });
        
        // CRITICAL FAILSAFE: Check for "Access denied" errors
        if (!resp?.success && isAccessDeniedError(resp)) {
          const failureCount = incrementAuthFailureCount();
          logger.log(` [FAILSAFE] Access denied error in tenant update handler. Failure count: ${failureCount}/${MAX_FAILURES}`);
          
          if (failureCount >= MAX_FAILURES) {
            logger.log(' [FAILSAFE] Maximum failures reached in tenant update - clearing session and redirecting to login');
            runFailsafeReLogin();
            return;
          }
          return;
        }
        
        if (resp?.success && resp.data) {
          // Reset failure counters on successful session fetch
          resetAuthFailureCounts();
          const updated = resp.data;
          setSession(updated);
          const prevCount = availableTenants?.length || 0;
          const sessionCount = Array.isArray(updated.availableTenants) ? updated.availableTenants.length : 0;
          // If availableTenants grew, update immediately
          if (sessionCount >= prevCount) {
            setAvailableTenants(updated.availableTenants || []);
          }
          if (updated.currentTenant) {
            // If primary/current changed, optionally toast
            if (currentTenant?.tenantId && updated.currentTenant.tenantId !== currentTenant.tenantId) {
              try {
                toast({ title: 'Switching to primary tenant…', description: updated.currentTenant.tenantName || updated.currentTenant.tenantSlug });
              } catch {}
            }
            setCurrentTenant(updated.currentTenant);
            localStorage.setItem('preferred_tenant_id', updated.currentTenant.tenantId!);
          } else {
            // No current tenant after refresh → require selection
            setCurrentTenant(null);
            setNeedsTenantSelection(true);
          }

          // If count did not increase (especially singleton), do a single throttled fetch (no loops) to avoid hammering
          if (sessionCount <= prevCount) {
            try {
              allowAvailableTenantsFetchRef.current = true;
              const list = await fetchAvailableTenantsThrottled();
              if (Array.isArray(list) && list.length > prevCount) {
                logger.log('AuthProviderClient: handler hydration success, availableTenants:', list);
                setAvailableTenants(list);
              }
            } catch (e) {
              console.warn('AuthProviderClient: handler hydration failed:', e);
            } finally {
              allowAvailableTenantsFetchRef.current = false;
            }
          }
        }
      } catch (e) {
        console.warn('AuthProviderClient: Failed to refresh session after bindings/config change:', e);
      }
    };
    // Removed onTenantCreated hydrator to avoid duplicate fetch storms
    window.addEventListener('tenantBindingsChanged', handler as EventListener);
    // Single authoritative trigger: configurationChanged
    window.addEventListener('configurationChanged', handler as EventListener);
    // Relationship changes can alter cross-tenant visibility
    window.addEventListener('relationshipChanged', handler as EventListener);
    return () => {
      window.removeEventListener('tenantBindingsChanged', handler as EventListener);
      window.removeEventListener('configurationChanged', handler as EventListener);
      window.removeEventListener('relationshipChanged', handler as EventListener);
    };
  }, [session?.sessionId, currentTenant?.tenantId, availableTenants?.length, createApiInstances, toast, runFailsafeReLogin]);

  // Visibility change awareness: recheck bindings when tab becomes visible
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        try { window.dispatchEvent(new Event('configurationChanged')); } catch {}
      }
    };
    try { document.addEventListener('visibilitychange', onVisibility); } catch {}
    return () => { try { document.removeEventListener('visibilitychange', onVisibility); } catch {} };
  }, []);

  // WebSocket: subscribe to backend push for available-tenants updates (with diagnostics + backoff)
  useEffect(() => {
    const wsRunId = ++webSocketEffectRunIdRef.current;
    const sessionId = resolveSessionIdForRequests() ?? session?.sessionId ?? null;
    const wsCorrelationId = `ws-run-${wsRunId}-sess-${(sessionId ?? 'none').slice(0, 8)}`;
    logger.log(' [WebSocket] Effect run started', {
      correlationId: wsCorrelationId,
      hasSessionId: Boolean(sessionId),
      previousConnectedSessionId: webSocketConnectedSessionIdRef.current,
    });

    // Tear down when session id changes or clears (React may batch null→new without cleanup otherwise).
    if (
      webSocketConnectedSessionIdRef.current &&
      webSocketConnectedSessionIdRef.current !== sessionId
    ) {
      logger.log(' [WebSocket] Session id changed — disconnecting stale connection', {
        from: webSocketConnectedSessionIdRef.current,
        to: sessionId,
      });
      webSocketServiceRef.current?.disconnect();
      webSocketServiceRef.current = null;
      webSocketConnectedSessionIdRef.current = null;
    }

    if (!sessionId) {
      logger.log(' [WebSocket] No session ID available, skipping WebSocket connection', {
        correlationId: wsCorrelationId,
      });
      return;
    }

    try {
      if (typeof window !== 'undefined' && localStorage.getItem('e2e_skip_session_websocket') === '1') {
        logger.log(' [WebSocket] Skipping session WebSocket (E2E test mode)', {
          correlationId: wsCorrelationId,
        });
        return;
      }
    } catch {
      // ignore storage access errors in non-browser contexts
    }
    
    const authToken = (session as MultiTenantSessionDto | null)?.accessToken || getAuthToken();
    if (!authToken) {
      logger.log(' [WebSocket] Waiting for Aegis session access token before connecting...', {
        correlationId: wsCorrelationId,
      });
      return;
    }

    if (document.readyState !== 'complete') {
      logger.log(' [WebSocket] Page not fully loaded yet; proceeding with guarded connect', {
        correlationId: wsCorrelationId,
      });
    }

    const activeService = webSocketServiceRef.current;

    // Reconnect when session id changed even if the old socket is still open.
    if (
      activeService &&
      activeService.isConnected() &&
      webSocketConnectedSessionIdRef.current === sessionId
    ) {
      logger.log(' [WebSocket] Already connected for this session, skipping new connection', {
        correlationId: wsCorrelationId,
        sessionId,
      });
      return;
    }

    // Clean up any existing connection before creating a new one
    if (activeService) {
      logger.log(' [WebSocket] Cleaning up existing connection before creating new one', {
        correlationId: wsCorrelationId,
        previousSessionId: webSocketConnectedSessionIdRef.current,
        nextSessionId: sessionId,
      });
      activeService.disconnect();
      webSocketServiceRef.current = null;
      webSocketConnectedSessionIdRef.current = null;
    }
    const wsUrl = normalizeWebSocketUrl(getDefaultApiBaseUrl() || '', sessionId, { contextPath: getApiContextPath() });
    
    const refreshSessionFromServer = async () => {
      try {
        const authoritativeSessionId = resolveSessionIdForRequests() ?? sessionId;
        if (!authoritativeSessionId) return;
        const token = getAuthToken();
        if (!token) return;
        const { sessionsApi } = createApiInstances(token);
        const resp = await sessionsApi.getSession({ sessionId: authoritativeSessionId });
        
        // CRITICAL FAILSAFE: Check for "Access denied" errors
        if (!resp?.success && isAccessDeniedError(resp)) {
          const failureCount = incrementAuthFailureCount();
          logger.log(` [FAILSAFE] Access denied error in WebSocket session refresh. Failure count: ${failureCount}/${MAX_FAILURES}`);
          
          if (failureCount >= MAX_FAILURES) {
            logger.log(' [FAILSAFE] Maximum failures reached in WebSocket refresh - clearing session and redirecting to login');
            runFailsafeReLoginRef.current();
            return;
          }
          return;
        }
        
        if (resp?.success && resp.data) {
          // Reset failure counters on successful session fetch
          resetAuthFailureCounts();
          const updated = resp.data;
          const prevCount = availableTenants?.length || 0;
          const newCount = updated.availableTenants?.length || 0;
          
          logger.log(' [WebSocket] Session refresh - updating availableTenants:', {
            previousCount: prevCount,
            newCount: newCount,
            availableTenants: updated.availableTenants
          });
          
          setSession(updated);
          
          // Always update availableTenants when we get fresh data from the server
          setAvailableTenants(updated.availableTenants || []);
          
          if (updated.currentTenant) {
            setCurrentTenant(updated.currentTenant);
            try { localStorage.setItem('preferred_tenant_id', updated.currentTenant.tenantId!); } catch {}
          } else {
            setCurrentTenant(null);
          }
          
          // Force a re-render by dispatching configuration change event
          try { window.dispatchEvent(new Event('configurationChanged')); } catch {}
          
          // If the count increased, also trigger a data refresh to ensure UI updates
          if (newCount > prevCount) {
            logger.log(' [WebSocket] Available tenants increased, triggering data refresh');
            setDataRefreshTrigger(prev => prev + 1);
          } else if (newCount === prevCount && prevCount === 1) {
            // Fallback: If we still have only 1 tenant after a session update,
            // force a fresh fetch of availableTenants to catch any backend timing issues
            logger.log(' [WebSocket] Fallback: Forcing fresh availableTenants fetch due to potential backend timing issue');
            setTimeout(async () => {
              try {
                const freshList = await fetchAvailableTenantsThrottled();
                if (Array.isArray(freshList) && freshList.length > prevCount) {
                  logger.log(' [WebSocket] Fallback fetch successful, availableTenants:', freshList);
                  setAvailableTenants(freshList);
                  setDataRefreshTrigger(prev => prev + 1);
                }
              } catch (e) {
                console.warn(' [WebSocket] Fallback fetch failed:', e);
              }
            }, 1000); // Wait 1 second for backend to catch up
          }
        }
      } catch (e) {
        console.warn('WebSocket session refresh failed', e);
      }
    };
    
    // Only create WebSocket service if we don't already have one or if the existing one can't connect
    if (activeService && activeService.canConnect()) {
      logger.log(' [WebSocket] Existing service can connect, reusing it', {
        correlationId: wsCorrelationId,
      });
      activeService.connect();
      return;
    }

    const wsService = new WebSocketService({
      url: wsUrl,
      token: authToken,
      onMessage: (message) => {
        // Handle different message types
        switch (message.type) {
          case 'session_update':
            logger.log(' [WebSocket] Session update received');
            // When session_update is received, the backend has already updated the session.
            // Just refresh the existing session to get the updated availableTenants.
            logger.log(' [WebSocket] Refreshing session after session_update to get updated availableTenants...');
            refreshSessionFromServer();
            break;
          case 'tenant_update':
            logger.log(' [WebSocket] Tenant update received');
            if (!firstDeliveryShownRef.current) {
              try {
                toast({ title: 'Live updates connected', description: 'You will see tenant updates instantly.' });
                firstDeliveryShownRef.current = true;
              } catch {}
            }
            // Tenant updates may affect availableTenants, so remint to ensure freshness
            logger.log(' [WebSocket] Reminting session after tenant_update to refresh availableTenants...');
            remintSessionRef.current().catch((error: any) => {
              logger.error(' [WebSocket] Failed to remint session after tenant_update:', error);
              refreshSessionFromServer();
            });
            break;
          case 'availableTenantsUpdated':
          case 'available-tenants-updated':
          case 'tenantsUpdated':
          case 'tenantBindingsUpdated':
          case 'tenantBindingsChanged':
          case 'relationshipsUpdated':
            logger.log(' [WebSocket] Tenant configuration update received:', message.type);
            if (!firstDeliveryShownRef.current) {
              try {
                toast({ title: 'Live updates connected', description: 'You will see tenant updates instantly.' });
                firstDeliveryShownRef.current = true;
              } catch {}
            }
            // When availableTenantsUpdated is received, remint the session to get fresh availableTenants
            // This ensures the session and availableTenants are fully updated after decommission/restore
            logger.log(' [WebSocket] Reminting session after tenant configuration update to refresh availableTenants...');
            remintSessionRef.current().catch((error: any) => {
              logger.error(' [WebSocket] Failed to remint session after tenant configuration update:', error);
              // Fallback to refresh if remint fails
              refreshSessionFromServer();
            });
            break;
        }
      },
      onError: (error) => {
        // Error is already logged by WebSocketService with suppression
        // No need to show user-facing error popups
        logger.log(' [WebSocket] Error handled gracefully (suppressed from user)');
      },
      onClose: (event) => {
        logger.log(' [WebSocket] Connection closed:', event.code, event.reason);
      },
      onOpen: () => {
        logger.log(' [WebSocket] Connection established');
      },
      onConnectionStatusChange: (status) => {
        // Optional: You can add a subtle connection status indicator here
        // For now, just log the status changes
        logger.log(' [WebSocket] Status changed to:', status);
      },
      onTokenInvalid: async (info) => {
        // SECURITY (pentest C01): WebSocket auth uses the Aegis-signed session access token,
        // not the OIDC IdP JWT. Prefer refreshing that token; OIDC silent renew cannot mint a
        // credential the socket will accept and often fails with login_required after prompt=none.
        logger.log(' [WebSocket] Session access token rejected by server, attempting Aegis token refresh', info);
        const sid = resolveSessionIdForRequests() ?? session?.sessionId ?? null;
        if (sid) {
          try {
            const refreshed = await refreshSessionAccessToken(sid);
            if (refreshed.success && refreshed.accessToken) {
              setAuthToken({ access_token: refreshed.accessToken, token_type: 'Bearer' });
              if (refreshed.session) {
                setSession((prev) =>
                  prev && prev.sessionId === sid ? { ...prev, ...refreshed.session } : prev
                );
              }
              const svc = webSocketServiceRef.current;
              if (svc) {
                svc.reconnectWithToken(refreshed.accessToken);
              }
              return;
            }
          } catch (e) {
            logger.warn(' [WebSocket] Aegis session access token refresh threw:', e);
          }
        }

        if (userManager) {
          const renewedUser = await trySilentRenewAndApply(userManager, silentRenewInFlightRef, {
            setUser,
            setAuthToken,
          });
          if (renewedUser) {
            // OIDC renew alone is insufficient post-C01; remint session so we get a fresh Aegis token.
            try {
              await remintSessionRef.current();
              const aegisToken = getAuthToken();
              if (aegisToken) {
                webSocketServiceRef.current?.reconnectWithToken(aegisToken);
                await completeOidcRenewalSuccess();
                return;
              }
            } catch (e) {
              logger.warn(' [WebSocket] Remint after OIDC silent renew failed:', e);
            }
          }
        }
        await resolveOidcRenewalFailure({
          sessionId: session?.sessionId,
          error: new Error('Authentication token could not be renewed. Please sign in again.'),
          validateSession,
          clearAuthForReLogin,
        });
      },
      onSessionInvalid: (info) => {
        try {
          window.dispatchEvent(
            new CustomEvent('sessionInvalid', { detail: { source: 'websocket', ...info } })
          );
        } catch {
          /* non-browser or dispatch blocked */
        }
      },
    });

    webSocketServiceRef.current = wsService;
    webSocketConnectedSessionIdRef.current = sessionId;
    logger.log(' [WebSocket] Created new WebSocketService instance', {
      correlationId: wsCorrelationId,
      wsUrl,
    });
    wsService.connect();

    return () => {
      logger.log(' [WebSocket] Effect cleanup', {
        correlationId: wsCorrelationId,
        ownsCurrentService: webSocketServiceRef.current === wsService,
      });
      if (webSocketServiceRef.current === wsService) {
        wsService.disconnect();
        webSocketServiceRef.current = null;
        webSocketConnectedSessionIdRef.current = null;
      }
    };
  }, [session?.sessionId, session?.accessToken, getAuthToken, createApiInstances, userManager, setAuthToken, setUser, completeOidcRenewalSuccess, clearAuthForReLogin]);

  // Cleanup WebSocket service on unmount
  useEffect(() => {
    return () => {
      if (webSocketServiceRef.current) {
        webSocketServiceRef.current.disconnect();
        webSocketServiceRef.current = null;
      }
    };
  }, []);

  // Cross-tab awareness and fast checks when network/focus returns
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'availableTenantsVersion') {
        try { window.dispatchEvent(new Event('configurationChanged')); } catch {}
      }
    };
    const onOnline = () => { try { window.dispatchEvent(new Event('configurationChanged')); } catch {} };
    const onFocus = () => { try { window.dispatchEvent(new Event('configurationChanged')); } catch {} };
    try { window.addEventListener('storage', onStorage); } catch {}
    try { window.addEventListener('online', onOnline); } catch {}
    try { window.addEventListener('focus', onFocus); } catch {}
    return () => {
      try { window.removeEventListener('storage', onStorage); } catch {}
      try { window.removeEventListener('online', onOnline); } catch {}
      try { window.removeEventListener('focus', onFocus); } catch {}
    };
  }, []);

  // Initial hydration removed: rely on WebSocket-only to avoid any polling or redundant fetches

  // Config awareness: detect visibility changes and show notice
  useEffect(() => {
    const currentIds = (availableTenants || []).map(t => t.tenantId!).filter(Boolean);
    if (!initialTenantsSetRef.current) {
      prevTenantIdsRef.current = currentIds;
      initialTenantsSetRef.current = true;
      return;
    }
    const prevIds = prevTenantIdsRef.current || [];
    // If previously there were more tenants and now fewer, show notice
    const prevSet = new Set(prevIds);
    const currSet = new Set(currentIds);
    const removed = [...prevSet].filter(id => !currSet.has(id));
    if (removed.length > 0) {
      try {
        toast({
          title: 'Tenant visibility updated',
          description: 'Some organizations are no longer visible due to configuration or binding changes.',
        });
      } catch {}
    }
    prevTenantIdsRef.current = currentIds;
  }, [availableTenants, toast]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('lastProcessedUser');
    };
  }, []);

  // Session validation effect (grace period after create/restore: trust session for UI gates, then validate)
  useEffect(() => {
    let cancelled = false;
    let graceTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const validateCurrentSession = async () => {
      if (cancelled) return;
      const sessionId = resolveSessionIdForRequests() ?? session?.sessionId;
      if (!sessionId) {
        setIsSessionValid(false);
        setIsSessionValidating(false);
        return;
      }
      if (!getShouldRunValidation(sessionId, sessionCreatedAtRef.current)) {
        // Session was just created/restored; treat as valid for gating (Domain Management, etc.)
        // until server validation runs after grace. Effect only keys on sessionId, so we must
        // schedule validation here—otherwise isSessionValid would stay false forever.
        setIsSessionValid(true);
        setIsSessionValidating(false);
        if (graceTimeoutId !== undefined) {
          clearTimeout(graceTimeoutId);
          graceTimeoutId = undefined;
        }
        const createdAt = sessionCreatedAtRef.current;
        const delayMs = Math.max(0, VALIDATION_GRACE_MS - (Date.now() - createdAt));
        graceTimeoutId = setTimeout(() => {
          graceTimeoutId = undefined;
          if (!cancelled) void validateCurrentSession();
        }, delayMs);
        return;
      }

      setIsSessionValidating(true);
      try {
        const result = await validateSession(sessionId);

        if (cancelled) return;

        // Handle "No session ID provided" error from validation
        if (!result.isValid && result.message?.includes('No session ID provided')) {
          // Don't redirect here - just mark as invalid
          // The auth-enforcer will handle showing login when appropriate
          setIsSessionValid(false);
          setIsSessionValidating(false);
          return;
        }

        // CRITICAL FIX: Full sign-out on expiry (OIDC + backend + cookies) — partial clearAuthForReLogin left SSO residual state (H01)
        if (!result.isValid && result.reason === 'expired') {
          logger.log(' [SESSION VALIDATION] Session expired - full sign-out');
          try {
            sessionStorage.setItem('force_prompt_login', 'true');
          } catch { /* ignore */ }
          triggerFullSignOut(
            'expired',
            new Error(result.message || 'Your session has expired due to inactivity. Please log in again.'),
            { skipIdpRedirect: false }
          );
          return;
        }

        // Revoked/other invalid: full sign-out so no residual auth binds the next login
        if (!result.isValid && (result.reason === 'revoked' || result.reason === 'error')) {
          logger.log(' [SESSION VALIDATION] Session invalid - full sign-out, reason:', result.reason);
          try {
            sessionStorage.setItem('force_prompt_login', 'true');
          } catch { /* ignore */ }
          triggerFullSignOut(
            result.reason === 'revoked' ? 'revoked' : 'invalid',
            new Error(result.message || 'Your session is no longer valid. Please log in again.'),
            { skipIdpRedirect: false }
          );
          return;
        }

        if (!result.isValid && result.reason === 'token_not_ready') {
          // Transitional state during startup/restore in some deployed environments.
          // Keep session valid for gating and allow subsequent validation to resolve.
          setIsSessionValid(true);
          return;
        }

        setIsSessionValid(result.isValid);
      } catch (error) {
        if (cancelled) return;
        console.error('Session validation failed:', error);
        // Do not force invalid on unexpected throws (transient errors); keep prior validity
      } finally {
        if (!cancelled) {
          setIsSessionValidating(false);
        }
      }
    };

    void validateCurrentSession();

    return () => {
      cancelled = true;
      if (graceTimeoutId !== undefined) {
        clearTimeout(graceTimeoutId);
        graceTimeoutId = undefined;
      }
    };
  }, [session?.sessionId, clearAuthForReLogin, triggerFullSignOut]);

  // CRITICAL FIX: Periodic session validation to detect expired sessions
  // Validates session every 5 minutes when authenticated; skipped during grace period after creation/restoration
  useEffect(() => {
    const authoritativeSessionId = resolveSessionIdForRequests() ?? session?.sessionId;
    if (!authoritativeSessionId || isLoading) return;
    if (!getShouldRunValidation(authoritativeSessionId, sessionCreatedAtRef.current)) return;

    const validatePeriodically = async () => {
      try {
        const sessionId = resolveSessionIdForRequests() ?? session?.sessionId;
        if (!sessionId) return;
        const result = await validateSession(sessionId);
        
        // Handle "No session ID provided" error from validation
        if (!result.isValid && result.message?.includes('No session ID provided')) {
          // Don't redirect here - just return
          // The auth-enforcer will handle showing login when appropriate
          return;
        }
        
        // Full sign-out on expiry (H01) — partial clear left SSO / HttpOnly cookie residual
        if (!result.isValid && result.reason === 'expired') {
          logger.log(' [PERIODIC VALIDATION] Session expired - full sign-out');
          try {
            sessionStorage.setItem('force_prompt_login', 'true');
          } catch { /* ignore */ }
          triggerFullSignOut(
            'expired',
            new Error(result.message || 'Your session has expired due to inactivity. Please log in again.'),
            { skipIdpRedirect: false }
          );
          return;
        }
        
        if (!result.isValid && result.reason === 'revoked') {
          logger.log(' [PERIODIC VALIDATION] Session revoked - full sign-out');
          try {
            sessionStorage.setItem('force_prompt_login', 'true');
          } catch { /* ignore */ }
          triggerFullSignOut('revoked', undefined, { skipIdpRedirect: false });
        }
      } catch (error) {
        console.error(' [PERIODIC VALIDATION] Error validating session:', error);
        // Don't clear session on validation errors - might be network issues
      }
    };

    // Validate immediately
    validatePeriodically();

    // Then validate every 5 minutes (300000ms)
    const intervalId = setInterval(validatePeriodically, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [session?.sessionId, isLoading, triggerFullSignOut]);

  /** Single derived auth phase for UI; see auth-state-machine.ts and AUTH_DESIGN_AND_HARDENING.md */
  const authPhase = useMemo(
    (): AuthPhase =>
      computeAuthPhase({
        pathname,
        user,
        session,
        currentTenant,
        error,
        isLoading,
      }),
    [pathname, user, session, currentTenant, error, isLoading]
  );

  // Update the context value
  const value = useMemo(() => ({
    user,
    session,
    availableTenants,
    currentTenant,
    lastTenantSwitchAt,
    isLoading,
    error,
    userManager,
    needsTenantSelection,
    needsEnrollment,
    pendingDomainConfig,
    dataRefreshTrigger,
    isSessionValid,
    isSessionValidating,
    sessionValidationStartedAtMs,
    authPhase,
    login,
    logout,
    selectTenant,
    switchTenant,
    refreshSessionAfterEnrollment,
    refreshSession: (opts?: { force?: boolean }) => refreshSessionData(opts ?? { force: true }),
    remintSession,
    ensureTenantAccess,
    updateSession,
    resetClientSession: clearStaleClientSessionForLogin,
  }), [
    user?.profile?.sub,
    user?.profile?.email, 
    session?.sessionId,
    session?.currentTenant?.tenantId,
    session?.currentTenant?.tenantSlug,
    session?.currentTenant?.tenantName,
    availableTenants.length,
    currentTenant?.tenantId,
    currentTenant?.tenantSlug,
    currentTenant?.tenantName,
    isLoading,
    error?.message,
    needsTenantSelection,
    needsEnrollment,
    dataRefreshTrigger,
    lastTenantSwitchAt,
    isSessionValid,
    isSessionValidating,
    sessionValidationStartedAtMs,
    authPhase
    // FIXED: Removed callback functions from dependencies since they're already memoized with useCallback
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


export function AuthProvider({ children }: { children: ReactNode }) {
  return <AuthProviderClient>{children}</AuthProviderClient>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
